/**
 * AllPages.jsx — all page components wired to the real API.
 * Every form uses controlled inputs, submit handlers, and calls
 * the server via src/services/api.js. No mock data imported.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge, Avatar, Card, CardHead, StatBox,
  Tabs, Modal, Drawer, Toggle, InfoRow, SectionLabel,
  Timeline, SplitBar, ProgressBar,
} from '../components/ui';
import {
  investors as investorsApi, returns as returnsApi,
  institutions as instApi,   deals as dealsApi,
  withdrawals as wdApi,
  audit as auditApi,         settings as settingsApi,
  dashboard as dashboardApi, reports, journal as journalApi, xero as xeroApi,
  fmt, fmtDate,
} from '../services/api';
import { UsersSection } from '../components/settings/UsersSection';

/* ─── shared data hook ─────────────────────────────────────────────────────── */
function useApi(fetchFn, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const load = useCallback(async () => {
    // If any dep is null/undefined, skip the fetch
    if (deps.some(d => d === null || d === undefined)) {
      setLoading(false);
      return;
    }
    setLoading(true); setError('');
    try   { const res = await fetchFn(); setData(res.data ?? res); }
    catch (e) { setError(e.message || 'Failed to load'); }
    finally   { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

const Loading = () => (
  <div style={{ padding:'36px 0', textAlign:'center', color:'var(--text3)', fontSize:13 }}>Loading…</div>
);
const Err = ({ msg, onRetry }) => (
  <div style={{ margin:'8px 0', padding:'12px 16px', background:'var(--danger-bg)', border:'1px solid var(--danger-border)', borderRadius:8, color:'var(--danger)', fontSize:13, display:'flex', gap:12, alignItems:'center' }}>
    <span style={{ flex:1 }}>⚠ {msg}</span>
    {onRetry && <button className="btn btn-o btn-sm" onClick={onRetry}>Retry</button>}
  </div>
);

const avatarColor = (id='') => {
  const p=['#24695c','#1a5f8a','#7a5200','#7a3540','#4a4a7a','#4a4a4a','#6b3080','#2d6e6e'];
  let h=0; for(let i=0;i<id.length;i++) h=(h*31+id.charCodeAt(i))&0xffffffff;
  return p[Math.abs(h)%p.length];
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* INVESTORS LIST                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const InvestorsPage = ({ onNavigate, showToast }) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm]     = useState({
    fullName:'', entityType:'individual', email:'', phone:'', idNumber:'', notes:'',
    depositAmount:'', depositDate:'', compounding:true,
  });
  const { data:rows, loading, error, reload } = useApi(
    () => investorsApi.list({ ...(status&&{status}), ...(search&&{search}) }),
    [status, search]
  );
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.type==='checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.fullName.trim()) { showToast('Full name is required','error'); return; }
    setSaving(true);
    try {
      const res = await investorsApi.create({
        fullName:form.fullName, entityType:form.entityType,
        email:form.email||undefined, phone:form.phone||undefined, idNumber:form.idNumber||undefined, notes:form.notes||undefined,
      });
      if (form.depositAmount && form.depositDate) {
        await investorsApi.addDeposit(res.data.id, {
          amount:parseFloat(form.depositAmount), depositDate:form.depositDate, compounding:form.compounding,
        });
      }
      showToast('Investor created');
      setOpen(false);
      setForm({ fullName:'', entityType:'individual', email:'', phone:'', idNumber:'', notes:'', depositAmount:'', depositDate:'', compounding:true });
      reload();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  return (
    <div>
      <div className="fbar">
        <input className="finput" style={{width:200}} type="text" placeholder="Search investors…" value={search} onChange={e=>setSearch(e.target.value)} />
        <select className="fsel" value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">All statuses</option><option value="active">Active</option>
          <option value="partial">Partial</option><option value="withdrawn">Withdrawn</option>
        </select>
        <div className="fs" />
        <button className="btn btn-o" onClick={()=>setOpen(true)}>+ Add investor</button>
      </div>
      {loading && <Loading />}
      {error   && <Err msg={error} onRetry={reload} />}
      {!loading && !error && (
        <Card>
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Investor</th><th>Type</th><th className="r">Total deposits</th><th className="r">Active capital</th><th className="r">Total returns</th><th>Next cycle</th><th style={{textAlign:'center'}}>Deps</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {!(rows||[]).length && <tr><td colSpan={9} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No investors yet</td></tr>}
                {(rows||[]).map(inv=>(
                  <tr key={inv.id} className="clickable" onClick={()=>onNavigate('inv-profile',{investorId:inv.id})}>
                    <td><div className="name-cell"><Avatar initials={(inv.full_name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()} color={avatarColor(inv.id)} /><div><div style={{fontWeight:600}}>{inv.full_name}</div><div style={{fontSize:'10.5px',color:'var(--text3)'}}>{inv.investor_code}</div></div></div></td>
                    <td style={{textTransform:'capitalize'}}>{inv.entity_type}</td>
                    <td className="r">{fmt(inv.total_deposits)}</td>
                    <td className="r fw">{fmt(inv.active_balance)}</td>
                    <td className="r" style={{color:'var(--success)',fontWeight:700}}>{fmt(inv.total_returns ?? inv.accrued_returns)}</td>
                    <td>{fmtDate(inv.next_cycle_date)}</td>
                    <td style={{textAlign:'center'}}>{inv.deposit_count}</td>
                    <td><Badge type={inv.status==='active'?'active':inv.status==='partial'?'warn':'neutral'}>{inv.status}</Badge></td>
                    <td><button className="card-action" onClick={e=>{e.stopPropagation();onNavigate('inv-profile',{investorId:inv.id})}}>View →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <Drawer open={open} onClose={()=>setOpen(false)} title="Add new investor">
        <div className="form-row fr2">
          <div><label className="field-label">Full display name *</label><input className="field-input" type="text" placeholder="e.g. Mr Mashologu" value={form.fullName} onChange={f('fullName')} /></div>
          <div><label className="field-label">Investor type</label><select className="field-sel" value={form.entityType} onChange={f('entityType')}><option value="individual">Individual</option><option value="trust">Trust</option><option value="company">Company</option><option value="other">Other</option></select></div>
        </div>
        <div className="form-row fr2">
          <div><label className="field-label">Email</label><input className="field-input" type="email" placeholder="investor@email.com" value={form.email} onChange={f('email')} /></div>
          <div><label className="field-label">Phone</label><input className="field-input" type="text" placeholder="+27 …" value={form.phone} onChange={f('phone')} /></div>
        </div>
        <div className="form-row"><div><label className="field-label">ID / Reg number</label><input className="field-input" type="text" placeholder="SA ID or company registration" value={form.idNumber} onChange={f('idNumber')} /></div></div>
        <div className="form-row"><div><label className="field-label">Notes</label><textarea className="field-ta" placeholder="Internal notes…" value={form.notes} onChange={f('notes')} /></div></div>
        <SectionLabel style={{marginBottom:12}}>Initial deposit (optional)</SectionLabel>
        <div className="form-row fr2">
          <div><label className="field-label">Deposit amount (ZAR)</label><input className="field-input" type="number" min="0" placeholder="0.00" value={form.depositAmount} onChange={f('depositAmount')} /></div>
          <div><label className="field-label">Deposit date</label><input className="field-input" type="date" value={form.depositDate} onChange={f('depositDate')} /></div>
        </div>
        <div className="form-row">
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <Toggle on={form.compounding} onChange={v=>setForm(p=>({...p,compounding:v}))} />
            <span style={{fontSize:12.5,color:'var(--text2)'}}>Compounding on (5% added to balance automatically every 60 days)</span>
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-o" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-p" onClick={save} disabled={saving}>{saving?'Saving…':'Create investor'}</button>
        </div>
      </Drawer>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* INVESTOR PROFILE                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* ─── Capital Statement & Projections — printable document ─────────────────── */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* CAPITAL STATEMENT — printable document matching exact PDF layout            */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════ */
/* CAPITAL STATEMENT — printable document matching exact CSV/PDF layout        */
/* ═══════════════════════════════════════════════════════════════════════════ */
function StmtDocument({ inv, investorId, showToast }) {
  const [stmtData, setStmtData] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [logoB64,  setLogoB64]  = useState('');

  useEffect(() => {
    if (!investorId) return;
    setLoading(true);
    reports.investorStatement(investorId)
      .then(res => setStmtData(res?.data || res))
      .catch(() => setStmtData(null))
      .finally(() => setLoading(false));
  }, [investorId]);

  useEffect(() => {
    fetch('/logo.webp')
      .then(r => r.blob())
      .then(b => new Promise(res => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); }))
      .then(d => setLogoB64(d))
      .catch(() => {});
  }, []);

  if (loading) return <Loading />;
  if (!stmtData) return (
    <div style={{padding:32,textAlign:'center',color:'var(--text3)',fontSize:13}}>
      No statement data — ensure this investor has completed return cycles.
    </div>
  );

  const summary           = stmtData.summary  || {};
  const fundingRows       = stmtData.fundingCycleTable || [];
  const grandTotal        = stmtData.grandTotal || {};
  const depList           = stmtData.deposits || [];
  const wdList            = stmtData.withdrawals || [];
  const now               = new Date().toLocaleDateString('en-ZA', { day:'numeric', month:'long', year:'numeric' });
  const closingDate       = summary.closingDate || now;

  const totalDeps  = parseFloat(grandTotal.total_deposits ?? summary.totalOriginalDeposits ?? 0);
  const totalWd    = parseFloat(grandTotal.total_withdrawn ?? summary.totalWithdrawn ?? 0);
  const activeBal  = parseFloat(grandTotal.active_capital_balance ?? summary.activeBalance ?? 0);
  const lifetimeReturns = parseFloat(grandTotal.total_returns_earned ?? summary.paidReturns ?? 0);
  const retPct          = summary.totalReturnPct || 0;

  // Format R amounts — negative shown as (R x,xxx.xx), zero/null returns ''
  const R = (n) => {
    if (n == null || n === '') return '';
    const num = Number(n);
    if (isNaN(num) || num === 0) return '';
    const abs = Math.abs(num);
    const s   = 'R\u00a0' + abs.toLocaleString('en-ZA', { minimumFractionDigits:2, maximumFractionDigits:2 });
    return num < 0 ? `(${s})` : s;
  };

  const printStatement = () => {
    const el = document.getElementById('stmt-print-area');
    if (!el) return;
    const w = window.open('', '_blank', 'width=1100,height=860');
    w.document.write(`<!DOCTYPE html><html><head><title>Capital Statement — ${inv?.full_name||''}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:10px;color:#111;background:#fff;padding:20px 28px}table{width:100%;border-collapse:collapse}th{background:#24695c;color:#fff;padding:6px 7px;font-size:8px;text-transform:uppercase;letter-spacing:.3px;border:1px solid #1d5549;white-space:nowrap}th.r{text-align:right}th.c{text-align:center}th.l{text-align:left}td{padding:4px 7px;border:1px solid #d5d5d5;font-size:9.5px;vertical-align:middle}.cn{font-weight:700;text-align:center;width:48px;border-right:2.5px solid #6b8f71;color:#222}.r{text-align:right}.l{text-align:left}.green{color:#2e7d32;font-weight:600}.red{color:#c0392b;font-weight:600}.sum-first td{background:#f0f7f5}.sum-first .cn{background:#dceede}.spacer td{background:#f9f9f9;color:#ccc;font-size:9px}.spacer .cn{background:#f9f9f9}.proj td{background:#fffbf0}.tot td{background:#24695c;color:#fff;font-weight:700;border-color:#1d5549}.hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;padding-bottom:9px;border-bottom:2.5px solid #6b8f71}.big{font-size:24px;font-weight:900;line-height:1}.sub{font-size:9px;letter-spacing:2px;text-transform:uppercase;color:#444;margin-top:2px}.intro{font-size:9.5px;line-height:1.7;text-align:justify;margin:9px 0 12px}.sec{font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#6b8f71;border-bottom:1.5px solid #6b8f71;padding-bottom:2px;margin:12px 0 6px}.ftr{margin-top:12px;border-top:1px solid #ddd;padding-top:6px;display:flex;justify-content:space-between;font-size:8px;color:#888}.wm{position:fixed;bottom:16px;right:16px;opacity:.04}@media print{body{padding:6px 12px}}</style></head><body>
    ${logoB64 ? `<div class="wm"><img src="${logoB64}" style="height:72px"/></div>` : ''}
    ${el.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 500);
  };

  const TH = (a='right') => ({background:'#24695c',color:'#fff',padding:'7px 8px',fontSize:9,textTransform:'uppercase',letterSpacing:'0.3px',border:'1px solid #1d5549',whiteSpace:'nowrap',textAlign:a});
  const TD = (a='right',x={}) => ({padding:'5px 8px',border:'1px solid #d5d5d5',textAlign:a,verticalAlign:'middle',fontSize:10.5,...x});
  const CN = (bg='#f0f0f0') => ({...TD('center'),fontWeight:700,fontSize:12,width:54,background:bg,borderRight:'2.5px solid #6b8f71',color:'#222'});
  const TOT = (a='right') => ({padding:'8px 8px',border:'1px solid #1d5549',background:'#24695c',color:'#fff',fontWeight:700,textAlign:a,fontSize:10.5});

  const renderFundingRow = (row, i) => {
    const isSummary     = row.rowKind === 'summary';
    const isDeposit   = row.rowKind === 'deposit';
    const isReturn    = row.rowKind === 'return';
    const isWithdrawal = row.rowKind === 'withdrawal';
    const isProj      = row.isProjection;
    const rowBg       = isSummary ? '#e8f2ef' : isDeposit ? '#f0f7f5' : isProj ? '#fffbf0' : '#fafafa';
    const cnBg        = isSummary ? '#24695c' : isDeposit ? '#dceede' : isProj ? '#fef5d8' : '#f5f5f5';
    const cnColor     = isSummary ? '#fff' : '#222';
    const detailPad   = (isDeposit || isReturn || isWithdrawal)
      ? { paddingLeft: 18, fontSize: 9.5, color: '#555', fontStyle: 'italic' }
      : {};

    return (
      <tr key={i} style={{ background: rowBg }}>
        <td style={{ ...CN(isSummary ? cnBg : '#fafafa'), color: cnColor, fontWeight: isSummary ? 700 : 400 }}>
          {isSummary ? (row.fundingCycleNumber || row.cycleNumber || '—') : ''}
        </td>
        <td style={TD('left', {
          background: rowBg,
          ...detailPad,
          whiteSpace: 'nowrap',
          fontWeight: isSummary ? 700 : isDeposit ? 600 : 400,
          color: isSummary ? '#1d5549' : undefined,
        })}>
          {row.monthLabel}
        </td>
        <td style={TD('right', { background: rowBg, color: row.deposits ? '#2e7d32' : '#ccc', fontWeight: (isSummary || row.deposits) ? 600 : 400 })}>
          {row.deposits ? R(row.deposits) : isSummary ? '-' : '-'}
        </td>
        <td style={TD('right', { background: rowBg, color: (isSummary || isReturn || isDeposit) && row.openingBalance ? '#666' : '#ccc' })}>
          {(isSummary || isReturn || isDeposit) && row.openingBalance != null && row.openingBalance !== '' ? R(row.openingBalance) : '-'}
        </td>
        <td style={TD('right', { background: rowBg, color: row.returnsEarned ? '#2e7d32' : '#ccc', fontWeight: row.returnsEarned ? 600 : 400 })}>
          {row.returnsEarned ? R(row.returnsEarned) : '-'}
        </td>
        <td style={TD('right', { background: rowBg, fontWeight: (isSummary || isReturn || isDeposit) && row.closingBalance != null ? 600 : 400, color: row.closingBalance ? '#111' : '#ccc' })}>
          {(isSummary || isReturn || isDeposit) && row.closingBalance != null ? R(row.closingBalance) : '-'}
        </td>
        <td style={TD('right', { background: rowBg, color: row.capitalWithdrawals ? '#c0392b' : '#ccc', fontWeight: row.capitalWithdrawals ? 600 : 400 })}>
          {R(row.capitalWithdrawals) || '-'}
        </td>
        <td style={TD('right', { background: rowBg, color: row.returnWithdrawals ? '#c0392b' : '#ccc', fontWeight: row.returnWithdrawals ? 600 : 400 })}>
          {R(row.returnWithdrawals) || '-'}
        </td>
        <td style={TD('right', { background: rowBg, fontWeight: 700, color: isSummary ? '#1d5549' : '#24695c' })}>
          {R(row.runningBalance) || '-'}
        </td>
      </tr>
    );
  };

  const tableHeader = (
    <thead>
      <tr>
        <th style={TH('center')}>Funding<br/>Cycle</th>
        <th style={TH('left')}>Transaction</th>
        <th style={TH()}>Deposits</th>
        <th style={TH()}>Opening<br/>Balance</th>
        <th style={TH()}>Returns<br/>Earned</th>
        <th style={TH()}>Closing<br/>Balance</th>
        <th style={TH()}>Capital<br/>Withdrawn</th>
        <th style={TH()}>Return<br/>Withdrawn</th>
        <th style={TH()}>Running<br/>Balance</th>
      </tr>
    </thead>
  );

  return (
    <div>
      <div style={{display:'flex',gap:8,marginBottom:14,justifyContent:'flex-end'}}>
        <button className="btn btn-p" onClick={printStatement}>🖨 Print / Save as PDF</button>
      </div>
      <div id="stmt-print-area" style={{fontFamily:'Arial,sans-serif',fontSize:10.5,color:'#111',background:'#fff'}}>

        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12,paddingBottom:10,borderBottom:'2.5px solid #6b8f71'}}>
          <img src="/logo.webp" alt="huhslCap" style={{height:106,width:'auto',objectFit:'contain'}} />
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:9,color:'#888',letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:2}}>Capital Statement &amp; Projections</div>
            <div style={{fontSize:13,fontWeight:700,color:'#222'}}>{inv?.full_name}</div>
            <div style={{fontSize:9,color:'#999'}}>{inv?.investor_code} · {now}</div>
          </div>
        </div>

        {/* Title */}
        <div style={{marginBottom:10}}>
          <div style={{fontSize:26,fontWeight:900,color:'#111',letterSpacing:'-0.5px',lineHeight:1}}>CAPITAL</div>
          <div style={{fontSize:10,color:'#444',letterSpacing:'2px',textTransform:'uppercase',marginTop:2}}>STATEMENT &amp; PROJECTIONS</div>
        </div>

        {/* Intro */}
        <p style={{fontSize:10.5,lineHeight:1.75,color:'#222',marginBottom:14,textAlign:'justify'}}>
          Your active capital balance is <strong>{fmt(activeBal)}</strong>
          {' '}(original deposits less capital withdrawn — returns excluded).
          {lifetimeReturns > 0 ? <> Total returns earned are <strong>{fmt(lifetimeReturns)}</strong>.</> : null}
          {' '}Original deposits total <strong>{fmt(totalDeps)}</strong>
          {totalWd > 0 ? <>, and <strong>{fmt(totalWd)}</strong> has been withdrawn in total</> : null}
          {' '}as at {closingDate}
          {retPct > 0 ? <>, representing a return of <strong style={{color:'#6b8f71'}}>{retPct}%</strong> (returns ÷ active capital)</> : null}.
        </p>

        {/* Section label */}
        <div style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.8px',color:'#6b8f71',borderBottom:'1.5px solid #6b8f71',paddingBottom:3,marginBottom:7}}>
          Funding Cycle History — All Deposits Combined (60-day cycles)
        </div>

        {fundingRows.length === 0 && (
          <div style={{padding:20,textAlign:'center',color:'#aaa',marginBottom:18,border:'1px solid #eee',borderRadius:4}}>
            No deposit activity — add deposits to generate statement tables.
          </div>
        )}

        {fundingRows.length > 0 && (
          <div style={{overflowX:'auto',marginBottom:18}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              {tableHeader}
              <tbody>
                {fundingRows.map((row, i) => renderFundingRow(row, i))}
              </tbody>
            </table>
          </div>
        )}

        {/* Deposit Schedule */}
        <div style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.8px',color:'#6b8f71',borderBottom:'1.5px solid #6b8f71',paddingBottom:3,marginBottom:7}}>
          Deposit Schedule
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',marginBottom:18}}>
          <thead>
            <tr>
              {[['Reference','left'],['Deposit Date','left'],['Original Amount','right'],['Active capital','right'],['Accrued Return','right'],['Rate','right'],['Next Cycle','left'],['Status','left']].map(([h,a])=>(
                <th key={h} style={TH(a)}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {depList.map((d,i) => (
              <tr key={d.id} style={{background:i%2===0?'#fff':'#fafafa'}}>
                <td style={TD('left',{fontFamily:'monospace',fontSize:9.5})}>{d.deposit_ref}</td>
                <td style={TD('left')}>{fmtDate(d.deposit_date)}</td>
                <td style={TD('right')}>{fmt(d.original_amount)}</td>
                <td style={TD('right',{fontWeight:600})}>{fmt(d.current_balance)}</td>
                <td style={TD('right',{color:'#2e7d32',fontWeight:600})}>{fmt(d.accrued_return)}</td>
                <td style={TD('right')}>{(parseFloat(d.return_rate||0)*100).toFixed(1)}%</td>
                <td style={TD('left')}>{fmtDate(d.next_cycle_date)}</td>
                <td style={TD('left')}>
                  <span style={{background:d.status==='active'?'#e8f5e9':'#eee',color:d.status==='active'?'#2e7d32':'#666',padding:'2px 8px',borderRadius:10,fontSize:8.5,fontWeight:600}}>
                    {d.status}
                  </span>
                </td>
              </tr>
            ))}
            {depList.length > 0 && (
              <tr>
                <td style={TOT('left')} colSpan={2}>Totals</td>
                <td style={TOT()}>{fmt(totalDeps)}</td>
                <td style={TOT()}>{fmt(activeBal)}</td>
                <td style={TOT()}>{fmt(lifetimeReturns)}</td>
                <td style={TOT()} colSpan={3} />
              </tr>
            )}
          </tbody>
        </table>

        {wdList.length > 0 && (
          <>
            <div style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.8px',color:'#6b8f71',borderBottom:'1.5px solid #6b8f71',paddingBottom:3,marginBottom:7,marginTop:4}}>
              Withdrawal Schedule
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',marginBottom:18}}>
              <thead>
                <tr>
                  {[['Reference','left'],['Date','left'],['Type','left'],['Amount','right'],['Status','left']].map(([h,a])=>(
                    <th key={h} style={TH(a)}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {wdList.map((w, idx) => (
                  <tr key={w.id} style={{background:idx%2===0?'#fff':'#fafafa'}}>
                    <td style={TD('left',{fontFamily:'monospace',fontSize:9.5})}>{w.withdrawal_ref}</td>
                    <td style={TD('left')}>{fmtDate(w.requested_date)}</td>
                    <td style={TD('left')}>{w.withdrawal_type?.replace(/_/g,' ')}</td>
                    <td style={TD('right',{color:'#c0392b',fontWeight:600})}>{fmt(w.amount)}</td>
                    <td style={TD('left')}>
                      <span style={{background:w.status==='processed'?'#e8f5e9':'#fff3e0',color:w.status==='processed'?'#2e7d32':'#e67e22',padding:'2px 8px',borderRadius:10,fontSize:8.5,fontWeight:600}}>
                        {w.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Footer */}
        <div style={{marginTop:14,borderTop:'1px solid #ddd',paddingTop:7,display:'flex',justifyContent:'space-between',fontSize:8.5,color:'#888'}}>
          <span>huhslCap Capital Management · Confidential — for {inv?.full_name} only · Not for distribution</span>
          <span>Generated {now} · {inv?.investor_code}</span>
        </div>
      </div>
    </div>
  );
}

/** Full calendar months from deposit date (UTC) to today (UTC), for return-withdrawal context. */
function monthsInvestedSinceDeposit(depositDateVal) {
  if (depositDateVal == null || depositDateVal === '') return 0;
  const raw = typeof depositDateVal === 'string'
    ? depositDateVal.slice(0, 10)
    : new Date(depositDateVal).toISOString().slice(0, 10);
  if (raw.length < 10) return 0;
  const [sy, sm, sd] = raw.split('-').map(n => parseInt(n, 10));
  if ([sy, sm, sd].some(n => Number.isNaN(n))) return 0;
  const now = new Date();
  const ey = now.getUTCFullYear();
  const em = now.getUTCMonth() + 1;
  const ed = now.getUTCDate();
  let months = (ey - sy) * 12 + (em - sm);
  if (ed < sd) months -= 1;
  return Math.max(0, months);
}

export const InvestorProfilePage = ({ selectedIds, showToast, onNavigate }) => {
  const investorId = selectedIds?.investorId;
  const [tab, setTab]                 = useState('Deposit ledger');
  const [depModal, setDepModal]       = useState(null);
  const [stmtModal, setStmtModal]     = useState(false);
  const [addDepOpen, setAddDepOpen]   = useState(false);
  const [wdOpen, setWdOpen]           = useState(false);
  const [wdDepositLock, setWdDepositLock] = useState(null); // { id, ref, accrued, months } when opened from a deposit row
  const [saving, setSaving]           = useState(false);
  const [depForm, setDepForm] = useState({ amount:'', depositDate:'', compounding:true, bankReference:'' });
  const [wdForm,  setWdForm]  = useState({ withdrawalType:'partial', amount:'', depositId:'', requestedDate:'', reason:'' });

  const { data:inv,  loading, error, reload }  = useApi(()=>investorsApi.get(investorId), [investorId]);
  const { data:deps, reload:reloadDeps }       = useApi(()=>investorsApi.deposits(investorId), [investorId]);

  if (!investorId) return <Err msg="Select an investor from the Investors list first" />;
  if (loading)     return <Loading />;
  if (error)       return <Err msg={error} onRetry={reload} />;
  if (!inv)        return <Err msg="Investor not found" />;

  const todayYmd = () => new Date().toISOString().slice(0, 10);

  const openReturnWithdrawalForDeposit = (d) => {
    const accrued = parseFloat(d.accrued_return || 0);
    const months = monthsInvestedSinceDeposit(d.deposit_date);
    setWdDepositLock({ id: d.id, ref: d.deposit_ref, accrued, months });
    setWdForm({
      withdrawalType: 'return_only',
      amount: accrued > 0 ? accrued.toFixed(2) : '',
      depositId: d.id,
      requestedDate: todayYmd(),
      reason: `Return-only withdrawal — ${d.deposit_ref} (${months} mo invested)`,
    });
    setWdOpen(true);
  };

  const openGeneralWithdrawal = () => {
    setWdDepositLock(null);
    setWdForm({ withdrawalType: 'partial', amount: '', depositId: '', requestedDate: todayYmd(), reason: '' });
    setWdOpen(true);
  };

  const closeWithdrawalDrawer = () => {
    setWdOpen(false);
    setWdDepositLock(null);
    setWdForm({ withdrawalType: 'partial', amount: '', depositId: '', requestedDate: '', reason: '' });
  };

  const saveWithdrawal = async () => {
    if (!wdForm.amount || !wdForm.requestedDate) { showToast('Amount and date are required','error'); return; }
    const amt = parseFloat(wdForm.amount);
    if (Number.isNaN(amt) || amt <= 0) { showToast('Enter a valid amount','error'); return; }
    if (wdForm.withdrawalType === 'return_only' && wdForm.depositId) {
      const cap = wdDepositLock && wdDepositLock.id === wdForm.depositId
        ? wdDepositLock.accrued
        : parseFloat((deps || []).find(x => x.id === wdForm.depositId)?.accrued_return || 0);
      if (amt > cap + 1e-6) {
        showToast('Return withdrawal cannot exceed accrued return on this deposit','error');
        return;
      }
    }
    setSaving(true);
    try {
      await wdApi.create({ investorId, depositId:wdForm.depositId||undefined, withdrawalType:wdForm.withdrawalType, amount:amt, requestedDate:wdForm.requestedDate, reason:wdForm.reason||undefined });
      showToast('Withdrawal request submitted');
      reloadDeps();
      reload();
      closeWithdrawalDrawer();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  const saveDeposit = async () => {
    if (!depForm.amount || !depForm.depositDate) { showToast('Amount and date are required','error'); return; }
    setSaving(true);
    try {
      await investorsApi.addDeposit(investorId, { amount:parseFloat(depForm.amount), depositDate:depForm.depositDate, compounding:depForm.compounding, bankReference:depForm.bankReference||undefined });
      showToast('Deposit added'); setAddDepOpen(false);
      setDepForm({amount:'',depositDate:'',compounding:true,bankReference:''});
      reloadDeps(); reload();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  const df = k => e => setDepForm(p=>({...p,[k]:e.target.type==='checkbox'?e.target.checked:e.target.value}));
  const wf = k => e => setWdForm(p=>({...p,[k]:e.target.value}));

  return (
    <div>
      <div className="hero-band">
        <div style={{display:'flex',alignItems:'center',gap:14,justifyContent:'space-between',flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <Avatar initials={(inv.full_name||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()} color="var(--accent)" size={50} />
            <div>
              <div className="hero-title">{inv.full_name}</div>
              <div className="hero-sub">{inv.investor_code} · {inv.entity_type} · Joined {fmtDate(inv.created_at)}</div>
              <div style={{marginTop:6}}><Badge type={inv.status==='active'?'active':inv.status==='partial'?'warn':'neutral'}>{inv.status}</Badge></div>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn" style={{background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.2)',fontSize:11.5}} onClick={()=>setStmtModal(true)}>Statement</button>
            <button className="btn" style={{background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.2)',fontSize:11.5}} onClick={()=>setAddDepOpen(true)}>+ Add deposit</button>
            <button className="btn" style={{background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.2)',fontSize:11.5}} onClick={openGeneralWithdrawal}>Withdrawal</button>
          </div>
        </div>
        <div className="hero-kpis">
          {[['Active capital',fmt(inv.active_balance)],['Total returns',fmt(inv.total_returns_earned ?? inv.accrued_returns)],
            // ['Total value',fmt(inv.total_value ?? (parseFloat(inv.active_balance||0)+parseFloat(inv.accrued_returns||0)))], // duplicates accrued returns on compounding deposits
            ['Deposits',inv.deposit_count||0],['Next payout',fmtDate(inv.next_cycle_date)],['Withdrawn',fmt(inv.total_withdrawn||0)]].map(([l,v])=>(
            <div className="hkpi" key={l}><div className="hkpi-val">{v}</div><div className="hkpi-label">{l}</div></div>
          ))}
        </div>
      </div>

      <div className="row3">
        <Card>
          <Tabs tabs={['Deposit ledger','Return cycles']} active={tab} onChange={setTab} />
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Ref</th><th>Date</th><th className="r">Amount</th><th className="r">Cycle balance</th><th className="r">Accrued return</th><th>Next cycle</th><th style={{textAlign:'center'}}>#</th><th>Compound</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {!(deps||[]).length && <tr><td colSpan={10} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No deposits yet</td></tr>}
                {(deps||[]).map(d=>(
                  <tr
                    key={d.id}
                    className="clickable"
                    title="Click to withdraw returns only from this deposit"
                    onClick={() => openReturnWithdrawalForDeposit(d)}
                  >
                    <td className="mono">{d.deposit_ref}</td>
                    <td>{fmtDate(d.deposit_date)}</td>
                    <td className="r">{fmt(d.original_amount)}</td>
                    <td className="r fw">{fmt(d.running_balance ?? d.current_balance)}</td>
                    <td className="r" style={{color:d.accrued_return>0?'var(--success)':'var(--text3)',fontWeight:700}}>{fmt(d.accrued_return)}</td>
                    <td>{fmtDate(d.next_cycle_date)}</td>
                    <td style={{textAlign:'center'}}>{d.cycle_number}</td>
                    <td><Badge type={d.compounding?'active':'neutral'}>{d.compounding?'On':'Off'}</Badge></td>
                    <td><Badge type={d.status==='active'?'active':d.status==='pending'?'info':'neutral'}>{d.status}</Badge></td>
                    <td><button type="button" className="card-action" onClick={e=>{ e.stopPropagation(); setDepModal(d); }}>Detail →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <div className="col">
          <Card>
            <CardHead title="Return projection" />
            <div className="card-body">
              <div className="stat-box" style={{marginBottom:10,background:'var(--brand-light)',borderColor:'var(--success-border)'}}>
                <div className="stat-label">Next payout estimate</div>
                <div className="stat-val" style={{color:'var(--brand)'}}>{fmt(inv.next_payout_estimate ?? parseFloat(inv.earning_balance||inv.active_balance||0)*0.05)}</div>
                <div className="stat-sub">5% on {fmt(inv.earning_balance ?? inv.active_balance)} (cycle balance)</div>
              </div>
              <ProgressBar value={42} max={60} label="Current cycle progress" sublabel="42 / 60 days" />
            </div>
          </Card>
          <Card>
            <CardHead title="Details" />
            <div className="card-body">
              <div className="info-rows">
                <InfoRow label="Email"    value={inv.email||'—'} />
                <InfoRow label="Phone"    value={inv.phone||'—'} />
                <InfoRow label="ID / Reg" value={inv.id_number||'—'} />
                <InfoRow label="Type"     value={<span style={{textTransform:'capitalize'}}>{inv.entity_type}</span>} />
                <InfoRow label="Status"   value={<Badge type={inv.status==='active'?'active':inv.status==='partial'?'warn':'neutral'}>{inv.status}</Badge>} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Deposit detail modal */}
      <Modal open={!!depModal} onClose={()=>setDepModal(null)} title={`Deposit — ${depModal?.deposit_ref||''}`}>
        {depModal && (
          <>
            <div className="stat-grid sg3" style={{marginBottom:14}}>
              <StatBox label="Original" value={fmt(depModal.original_amount)} />
              <StatBox label="Cycle balance" value={fmt(depModal.running_balance ?? depModal.current_balance)} sub="Base for next 5% return" />
              <StatBox label="Net capital" value={fmt(depModal.current_balance)} />
              <StatBox label="Accrued"  value={fmt(depModal.accrued_return)} />
            </div>
            <div className="info-rows">
              <InfoRow label="Deposit date"   value={fmtDate(depModal.deposit_date)} />
              <InfoRow label="Next cycle"     value={<span style={{color:'var(--brand)'}}>{fmtDate(depModal.next_cycle_date)}</span>} />
              <InfoRow label="Cycle number"   value={depModal.cycle_number} />
              <InfoRow label="Return rate"    value={`${((depModal.return_rate||0.05)*100).toFixed(1)}% per ${depModal.cycle_days||60} days`} />
              <InfoRow label="Compounding"    value={depModal.compounding?'On':'Off'} />
              <InfoRow label="Bank reference" value={depModal.bank_reference||'—'} />
              <InfoRow label="Status"         value={<Badge type={depModal.status==='active'?'active':'info'}>{depModal.status}</Badge>} />
            </div>
          </>
        )}
      </Modal>

      {/* ── Capital Statement & Projections Modal ── */}
      <Modal open={stmtModal} onClose={()=>setStmtModal(false)} title="Capital Statement & Projections" width={860}>
        <StmtDocument inv={inv} investorId={investorId} showToast={showToast} />
      </Modal>

      {/* Add deposit drawer */}
      <Drawer open={addDepOpen} onClose={()=>setAddDepOpen(false)} title={`Add deposit — ${inv.full_name}`}>
        <div style={{background:'var(--brand-light)',borderRadius:7,padding:'10px 12px',marginBottom:16,fontSize:12,color:'var(--brand-dark)'}}>Adds a new deposit. Return cycle starts 60 days from deposit date.</div>
        <div className="form-row fr2">
          <div><label className="field-label">Amount (ZAR) *</label><input className="field-input" type="number" min="1" placeholder="0.00" value={depForm.amount} onChange={df('amount')} /></div>
          <div><label className="field-label">Deposit date *</label><input className="field-input" type="date" value={depForm.depositDate} onChange={df('depositDate')} /></div>
        </div>
        <div className="form-row"><div><label className="field-label">Bank reference</label><input className="field-input" type="text" placeholder="Proof of payment ref" value={depForm.bankReference} onChange={df('bankReference')} /></div></div>
        <div className="form-row">
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <Toggle on={depForm.compounding} onChange={v=>setDepForm(p=>({...p,compounding:v}))} />
            <span style={{fontSize:12.5,color:'var(--text2)'}}>Compounding on (5% added to balance automatically every 60 days)</span>
          </div>
        </div>
        {depForm.depositDate && (
          <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,padding:'9px 14px',fontSize:12,color:'var(--text2)',marginBottom:14}}>
            <strong>First cycle date:</strong> {fmtDate(new Date(new Date(depForm.depositDate).getTime()+60*86400000).toISOString().slice(0,10))} &nbsp;·&nbsp; <strong>Rate:</strong> 5%
          </div>
        )}
        <div className="form-actions">
          <button className="btn btn-o" onClick={()=>setAddDepOpen(false)}>Cancel</button>
          <button className="btn btn-p" onClick={saveDeposit} disabled={saving}>{saving?'Saving…':'Save deposit'}</button>
        </div>
      </Drawer>

      {/* Withdrawal drawer */}
      <Drawer open={wdOpen} onClose={closeWithdrawalDrawer} title={wdDepositLock ? `Withdraw return — ${wdDepositLock.ref}` : 'New withdrawal request'}>
        <div style={{background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:7,padding:'10px 12px',marginBottom:16,fontSize:12,color:'var(--warning)'}}>Requires Finance approval before funds are released.</div>
        {wdDepositLock && (
          <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,padding:'10px 12px',marginBottom:16,fontSize:12.5,color:'var(--text2)',lineHeight:1.55}}>
            <strong>Deposit:</strong> {wdDepositLock.ref} &nbsp;·&nbsp; <strong>Months invested:</strong> {wdDepositLock.months} &nbsp;·&nbsp; <strong>Accrued return available:</strong>{' '}
            <span style={{color:wdDepositLock.accrued>0?'var(--success)':'var(--text3)',fontWeight:700}}>{fmt(wdDepositLock.accrued)}</span>
            {wdDepositLock.accrued <= 0 && (
              <div style={{marginTop:8,color:'var(--danger)',fontWeight:600}}>No accrued return to withdraw yet on this deposit.</div>
            )}
          </div>
        )}
        <div className="form-row fr2">
          <div><label className="field-label">Withdrawal type</label><select className="field-sel" value={wdForm.withdrawalType} onChange={wf('withdrawalType')} disabled={!!wdDepositLock}><option value="partial">Partial</option><option value="full_deposit">Full deposit</option><option value="return_only">Return only</option><option value="full_exit">Full exit</option></select></div>
          <div><label className="field-label">Amount (ZAR) *</label><input className="field-input" type="number" min="0.01" step="0.01" placeholder="0.00" value={wdForm.amount} onChange={wf('amount')} {...(wdDepositLock && wdDepositLock.accrued > 0 ? { max: wdDepositLock.accrued } : {})} /></div>
        </div>
        <div className="form-row"><div><label className="field-label">Deposit (optional)</label><select className="field-sel" value={wdForm.depositId} onChange={wf('depositId')} disabled={!!wdDepositLock}><option value="">— All / not specific —</option>{(deps||[]).filter(d=>d.status!=='withdrawn').map(d=><option key={d.id} value={d.id}>{d.deposit_ref} · {fmt(d.current_balance)}</option>)}</select></div></div>
        <div className="form-row fr2"><div><label className="field-label">Requested date *</label><input className="field-input" type="date" value={wdForm.requestedDate} onChange={wf('requestedDate')} /></div></div>
        <div className="form-row"><div><label className="field-label">Reason</label><textarea className="field-ta" placeholder="Reason for withdrawal…" value={wdForm.reason} onChange={wf('reason')} /></div></div>
        <div className="form-actions">
          <button className="btn btn-o" onClick={closeWithdrawalDrawer}>Cancel</button>
          <button className="btn btn-p" onClick={saveWithdrawal} disabled={saving || (wdDepositLock && wdDepositLock.accrued <= 0)}>{saving?'Submitting…':'Submit for approval'}</button>
        </div>
      </Drawer>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* LEGACY POOL                                                                 */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const LegacyPoolPage = ({ onNavigate }) => {
  const { data:p, loading, error, reload } = useApi(()=>dashboardApi.legacyPool());
  const { data:dealRows }                  = useApi(()=>dealsApi.list({source:'legacy'}));
  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  const pool = p || {};
  return (
    <div>
      <div className="stat-grid sg4" style={{marginBottom:14}}>
        <StatBox label="Total active capital" value={fmt(pool.totalPooledCapital)} sub={`Net deposits less capital withdrawn · ${pool.activeInvestors||0} investors`} accentLeft />
        <StatBox label="Total returns" value={fmt(pool.totalReturns)} sub="Earned across all investors" accentLeft accentColor="var(--success)" />
        <StatBox label="Deployed in deals" value={fmt(pool.deployed)} sub={`${pool.utilisationPct||0}% of pool value`} />
        <StatBox
          label={pool.overUtilised ? 'Over-utilised by' : 'Available'}
          value={
            pool.overUtilised
              ? <span style={{color:'var(--danger)'}}>{fmt(pool.overUtilisedAmount)}</span>
              : <span style={{color: pool.available > 0 ? 'var(--success)' : 'var(--text3)'}}>{fmt(pool.available)}</span>
          }
          sub={pool.overUtilised ? 'Capital + returns below deployed' : 'Capital + returns − deployed'}
        />
      </div>
      <div className="stat-grid sg2" style={{marginBottom:14}}>
        <StatBox label="Total pool value" value={fmt(pool.totalPoolValue ?? ((parseFloat(pool.totalPooledCapital)||0)+(parseFloat(pool.accruedReturns)||0)))} sub="Active capital + accrued returns" />
        <StatBox label="Return obligation 30d" value={<span style={{color:'var(--warning)'}}>{fmt(pool.returnObligation30d)}</span>} sub={`Due now: ${fmt(pool.returnObligationDueNow||0)}`} accentLeft accentColor="var(--accent)" />
      </div>
      <div className="row2">
        <Card>
          <CardHead title="Pool allocation by deal" action onAction={()=>onNavigate('allocation')} actionLabel="Full view" />
          <table className="tbl">
            <thead><tr><th>Deal</th><th>Client</th><th className="r">Legacy alloc.</th><th>Split</th><th>Status</th></tr></thead>
            <tbody>
              {(dealRows||[]).filter(d=>d.legacy_allocation>0).length===0 && <tr><td colSpan={5} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>No legacy allocations yet</td></tr>}
              {(dealRows||[]).filter(d=>d.legacy_allocation>0).map(d=>(
                <tr key={d.id} className="clickable" onClick={()=>onNavigate('deal-detail',{dealId:d.id})}>
                  <td className="mono">{d.deal_ref}</td><td>{d.client_name}</td>
                  <td className="r fw">{fmt(d.legacy_allocation)}</td>
                  <td><SplitBar legacyPct={parseFloat(d.total_allocated)>0?Math.round(parseFloat(d.legacy_allocation)/parseFloat(d.total_allocated)*100):0} instPct={parseFloat(d.total_allocated)>0?Math.round(parseFloat(d.institutional_allocation)/parseFloat(d.total_allocated)*100):0} /></td>
                  <td><Badge type={d.status==='active'?'active':d.status==='overdue'?'danger':'warn'}>{d.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card>
          <CardHead title="Pool utilisation" />
          <div className="card-body">
            <ProgressBar value={pool.utilisationPct||0} max={100} label="Deployed" sublabel={`${pool.utilisationPct||0}%`} />
            <div className="info-rows" style={{marginTop:12}}>
              <InfoRow label="Active capital"  value={fmt(pool.totalPooledCapital)} />
              <InfoRow label="Total returns"   value={<span style={{color:'var(--success)'}}>{fmt(pool.totalReturns)}</span>} />
              <InfoRow label="Accrued returns" value={<span style={{color:'var(--success)'}}>{fmt(pool.accruedReturns)}</span>} />
              <InfoRow label="Pool value"      value={fmt(pool.totalPoolValue)} />
              <InfoRow label="Deployed"        value={fmt(pool.deployed)} />
              <InfoRow label={pool.overUtilised ? 'Over-utilised' : 'Available'} value={
                pool.overUtilised
                  ? <span style={{color:'var(--danger)'}}>{fmt(pool.overUtilisedAmount)}</span>
                  : <span style={{color:'var(--success)'}}>{fmt(pool.available)}</span>
              } />
              <InfoRow label="Due next 30d"    value={<span style={{color:'var(--warning)'}}>{fmt(pool.returnObligation30d)}</span>} />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* RETURNS ENGINE                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const ReturnsPage = ({ showToast }) => {
  const [runModal, setRunModal] = useState(false);
  const [running, setRunning]   = useState(false);
  const { data:upcoming, loading, error, reload } = useApi(()=>returnsApi.upcoming(30));
  const { data:history }                          = useApi(()=>returnsApi.history(20));

  const runCycle = async () => {
    setRunning(true);
    try { const r=await returnsApi.runCycle(); showToast(r.message||'Cycle complete'); setRunModal(false); reload(); }
    catch(e) { showToast(e.message||'Error','error'); }
    finally  { setRunning(false); }
  };

  return (
    <div>
      <div className="stat-grid sg4">
        <StatBox label="Due now"       value={<span style={{color:'var(--warning)'}}>{fmt(upcoming?.summary?.totalDue||0)}</span>} sub={`${(upcoming?.data||[]).filter(r=>r.status==='due').length} cycles`} accentLeft />
        <StatBox label="Due in 30d"    value={fmt((upcoming?.summary?.totalDue||0)+(upcoming?.summary?.totalUpcoming||0))} sub={`${upcoming?.summary?.count||0} total cycles`} />
        <StatBox label="Return rate"   value="5%" sub="per 60-day cycle" />
        <StatBox label="Cycle length"  value="60 days" sub="configurable in settings" />
      </div>
      <div className="row2">
        <Card>
          <CardHead title="Upcoming return cycles" action={<button className="btn btn-p btn-sm" onClick={()=>setRunModal(true)}>Run return cycle</button>} />
          {loading && <div style={{padding:20}}><Loading /></div>}
          {error   && <div style={{padding:16}}><Err msg={error} /></div>}
          {!loading && !error && (
            <table className="tbl">
              <thead><tr><th>Deposit</th><th>Investor</th><th className="r">Balance</th><th className="r">Return (5%)</th><th>Due date</th><th>Cycle</th><th>Status</th></tr></thead>
              <tbody>
                {!(upcoming?.data||[]).length && <tr><td colSpan={7} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>No cycles due in 30 days</td></tr>}
                {(upcoming?.data||[]).map(r=>(
                  <tr key={r.id}>
                    <td className="mono">{r.deposit_ref}</td><td>{r.investor_name}</td>
                    <td className="r">{fmt(r.balance_at_time)}</td>
                    <td className="r fw" style={{color:'var(--success)'}}>{fmt(r.return_amount)}</td>
                    <td style={{color:r.status==='due'?'var(--danger)':''}}>{fmtDate(r.due_date)}</td>
                    <td style={{textAlign:'center'}}>{r.cycle_number}</td>
                    <td><Badge type={r.status==='due'?'warn':'info'}>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card>
          <CardHead title="Paid cycle history" />
          <table className="tbl">
            <thead><tr><th>Deposit</th><th>Investor</th><th className="r">Amount</th><th>Paid date</th><th>Status</th></tr></thead>
            <tbody>
              {!(history?.data||[]).length && <tr><td colSpan={5} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>No paid cycles yet</td></tr>}
              {(history?.data||[]).map(r=>(
                <tr key={r.id}><td className="mono">{r.deposit_ref}</td><td>{r.investor_name}</td><td className="r fw">{fmt(r.return_amount)}</td><td>{fmtDate(r.paid_at)}</td><td><Badge type="active">Paid</Badge></td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <Modal open={runModal} onClose={()=>setRunModal(false)} title="Run return cycle">
        <div style={{background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:8,padding:'12px 14px',marginBottom:16,fontSize:12.5,color:'var(--warning)'}}>
          This will process all deposits with a cycle due today or earlier and is recorded in the audit log.
        </div>
        <div className="info-rows" style={{marginBottom:14}}>
          <InfoRow label="Cycles to process" value={<strong>{(upcoming?.data||[]).filter(r=>r.status==='due').length}</strong>} />
          <InfoRow label="Total payout" value={<span style={{color:'var(--success)',fontWeight:700}}>{fmt(upcoming?.summary?.totalDue||0)}</span>} />
        </div>
        <div className="form-actions">
          <button className="btn btn-o" onClick={()=>setRunModal(false)}>Cancel</button>
          <button className="btn btn-p" onClick={runCycle} disabled={running}>{running?'Processing…':'Confirm and run'}</button>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* INSTITUTIONS LIST                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const InstitutionsPage = ({ onNavigate, showToast }) => {
  const [open, setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm]   = useState({ name:'', institutionType:'commercial_bank', facilityType:'revolving_credit', approvedFacility:'', interestRateDesc:'', interestRatePct:'', termMonths:'', startDate:'', maturityDate:'', security:'', notes:'' });
  const { data:rows, loading, error, reload } = useApi(()=>instApi.list());
  const f = k => e => setForm(p=>({...p,[k]:e.target.value}));

  const save = async () => {
    if (!form.name || !form.approvedFacility) { showToast('Name and facility size required','error'); return; }
    setSaving(true);
    try {
      await instApi.create({ name:form.name, institutionType:form.institutionType, facilityType:form.facilityType, approvedFacility:parseFloat(form.approvedFacility), interestRateDesc:form.interestRateDesc||undefined, interestRatePct:form.interestRatePct?(parseFloat(form.interestRatePct)/100):undefined, termMonths:form.termMonths?parseInt(form.termMonths):undefined, startDate:form.startDate||undefined, maturityDate:form.maturityDate||undefined, security:form.security||undefined, notes:form.notes||undefined });
      showToast('Institution created'); setOpen(false);
      setForm({name:'',institutionType:'commercial_bank',facilityType:'revolving_credit',approvedFacility:'',interestRateDesc:'',interestRatePct:'',termMonths:'',startDate:'',maturityDate:'',security:'',notes:''});
      reload();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  return (
    <div>
      <div className="fbar"><div className="fs" /><button className="btn btn-o" onClick={()=>setOpen(true)}>+ Add institution</button></div>
      {(rows||[]).length===0 && <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>No institutions yet</div>}
      {(rows||[]).map(inst=>(
        <div className="inst-card" key={inst.id}>
          <div className="ic-header">
            <div><div className="ic-name">{inst.name}</div><div className="ic-type">{inst.institution_type?.replace(/_/g,' ')} · {inst.facility_type?.replace(/_/g,' ')} · {inst.institution_code}</div></div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <Badge type={inst.status==='active'?'active':inst.status==='review'?'warn':'neutral'}>{inst.status}</Badge>
              <button className="btn btn-o btn-sm" onClick={()=>onNavigate('inst-profile',{instId:inst.id})}>View →</button>
            </div>
          </div>
          <div className="ic-stats">
            <div><div className="ics-label">Approved</div><div className="ics-val">{fmt(inst.approved_facility)}</div></div>
            <div><div className="ics-label">Drawn</div><div className="ics-val">{fmt(inst.amount_drawn)}</div></div>
            <div><div className="ics-label">Available</div><div className="ics-val" style={{color:'var(--success)'}}>{fmt(inst.available_facility)}</div></div>
            <div><div className="ics-label">Rate</div><div className="ics-val">{inst.interest_rate_desc||'—'}</div></div>
            <div><div className="ics-label">Next repayment</div><div className="ics-val" style={{color:inst.status==='active'&&inst.next_repayment_date?'var(--danger)':''}}>{fmtDate(inst.next_repayment_date)}</div></div>
          </div>
          <ProgressBar value={parseFloat(inst.utilisation_pct||0)} max={100} variant="a" label="Utilisation" sublabel={`${inst.utilisation_pct||0}%`} />
        </div>
      ))}
      <Drawer open={open} onClose={()=>setOpen(false)} title="Add new institution">
        <div className="form-row"><div><label className="field-label">Institution name *</label><input className="field-input" type="text" placeholder="e.g. FNB Commercial Finance" value={form.name} onChange={f('name')} /></div></div>
        <div className="form-row fr2">
          <div><label className="field-label">Type</label><select className="field-sel" value={form.institutionType} onChange={f('institutionType')}><option value="commercial_bank">Commercial bank</option><option value="development_bank">Development bank</option><option value="private_lender">Private lender</option><option value="other">Other</option></select></div>
          <div><label className="field-label">Facility type</label><select className="field-sel" value={form.facilityType} onChange={f('facilityType')}><option value="revolving_credit">Revolving credit</option><option value="term_loan">Term loan</option><option value="asset_backed">Asset-backed</option><option value="mezzanine">Mezzanine</option></select></div>
        </div>
        <div className="form-row fr2">
          <div><label className="field-label">Approved facility (ZAR) *</label><input className="field-input" type="number" min="1" placeholder="0.00" value={form.approvedFacility} onChange={f('approvedFacility')} /></div>
          <div><label className="field-label">Interest rate (%)</label><input className="field-input" type="number" step="0.01" placeholder="e.g. 11.25" value={form.interestRatePct} onChange={f('interestRatePct')} /></div>
        </div>
        <div className="form-row fr2">
          <div><label className="field-label">Rate description</label><input className="field-input" type="text" placeholder="e.g. Prime + 2.5%" value={form.interestRateDesc} onChange={f('interestRateDesc')} /></div>
          <div><label className="field-label">Term (months)</label><input className="field-input" type="number" placeholder="24" value={form.termMonths} onChange={f('termMonths')} /></div>
        </div>
        <div className="form-row fr2">
          <div><label className="field-label">Start date</label><input className="field-input" type="date" value={form.startDate} onChange={f('startDate')} /></div>
          <div><label className="field-label">Maturity date</label><input className="field-input" type="date" value={form.maturityDate} onChange={f('maturityDate')} /></div>
        </div>
        <div className="form-row"><div><label className="field-label">Security / collateral</label><input className="field-input" type="text" placeholder="e.g. Cession of debtors" value={form.security} onChange={f('security')} /></div></div>
        <div className="form-actions">
          <button className="btn btn-o" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-p" onClick={save} disabled={saving}>{saving?'Saving…':'Create institution'}</button>
        </div>
      </Drawer>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* INSTITUTION PROFILE                                                         */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const InstProfilePage = ({ selectedIds, showToast }) => {
  const instId = selectedIds?.instId;
  const [tab, setTab]       = useState('Amortisation schedule');
  const [saving, setSaving] = useState(false);
  const [ddForm, setDdForm] = useState({ amount:'', drawDate:'', purpose:'' });
  const { data:inst,  loading, error, reload } = useApi(()=>instApi.get(instId),         [instId]);
  const { data:amort, reload:reloadAmort }     = useApi(()=>instApi.amortisation(instId), [instId]);
  const { data:draws, reload:reloadDraws }     = useApi(()=>instApi.drawdowns(instId),    [instId]);

  if (!instId) return <Err msg="Select an institution from the list first" />;
  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  if (!inst)   return <Err msg="Institution not found" />;

  const df = k => e => setDdForm(p=>({...p,[k]:e.target.value}));

  const saveDrawdown = async () => {
    if (!ddForm.amount || !ddForm.drawDate) { showToast('Amount and date required','error'); return; }
    setSaving(true);
    try {
      await instApi.addDrawdown(instId, { amount:parseFloat(ddForm.amount), drawDate:ddForm.drawDate, purpose:ddForm.purpose||undefined });
      showToast('Drawdown recorded'); setDdForm({amount:'',drawDate:'',purpose:''});
      reload(); reloadDraws();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  const genAmort = async () => {
    setSaving(true);
    try { await instApi.generateAmortisation(instId); showToast('Schedule generated'); reloadAmort(); }
    catch(e) { showToast(e.message||'Error','error'); }
    finally  { setSaving(false); }
  };

  const markPaid = async (rowId) => {
    try { await instApi.markInstalment(instId, rowId, {status:'paid'}); showToast('Marked paid'); reloadAmort(); }
    catch(e) { showToast(e.message||'Error','error'); }
  };

  return (
    <div>
      <div className="deal-hero" style={{borderRadius:10,marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div><div style={{fontSize:17,fontWeight:700}}>{inst.name}</div><div style={{fontSize:11.5,opacity:0.6,marginTop:2}}>{inst.facility_type?.replace(/_/g,' ')} · {inst.institution_code} · {inst.institution_type?.replace(/_/g,' ')}</div></div>
          <Badge type={inst.status==='active'?'active':inst.status==='review'?'warn':'neutral'}>{inst.status}</Badge>
        </div>
        <div className="deal-kpi-row">
          {[['Approved',fmt(inst.approved_facility)],['Drawn',fmt(inst.amount_drawn)],['Available',fmt(inst.available_facility)],['Utilisation',`${inst.utilisation_pct||0}%`],['Rate',inst.interest_rate_desc||'—']].map(([l,v])=>(
            <div className="dkpi" key={l}><div className="dkpi-label">{l}</div><div className="dkpi-val">{v}</div></div>
          ))}
        </div>
      </div>
      <div className="row3">
        <Card>
          <Tabs tabs={['Amortisation schedule','Drawdown history']} active={tab} onChange={setTab} />
          {tab==='Amortisation schedule' && (
            (amort||[]).length===0 ? (
              <div style={{padding:24,textAlign:'center'}}>
                <div style={{color:'var(--text3)',marginBottom:12,fontSize:13}}>No schedule yet</div>
                <button className="btn btn-p" onClick={genAmort} disabled={saving}>{saving?'Generating…':'Generate amortisation schedule'}</button>
              </div>
            ) : (
              <table className="tbl">
                <thead><tr><th>Date</th><th>#</th><th className="r">Principal</th><th className="r">Interest</th><th className="r">Total</th><th className="r">Balance</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {(amort||[]).map(a=>(
                    <tr key={a.id} className={a.status==='paid'?'amort-paid':a.status==='due'?'amort-due':''}>
                      <td>{a.due_date}</td><td>{a.instalment_no}</td>
                      <td className="r">{fmt(a.principal)}</td><td className="r">{fmt(a.interest)}</td>
                      <td className="r">{fmt(a.total_due)}</td><td className="r">{fmt(a.balance_after)}</td>
                      <td><Badge type={a.status==='paid'?'neutral':a.status==='due'?'danger':'info'}>{a.status==='due'?'Due':a.status}</Badge></td>
                      <td>{a.status!=='paid'&&<button className="card-action" onClick={()=>markPaid(a.id)}>Mark paid</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          {tab==='Drawdown history' && (
            <table className="tbl">
              <thead><tr><th>Ref</th><th>Date</th><th className="r">Amount</th><th>Purpose</th></tr></thead>
              <tbody>
                {!(draws||[]).length&&<tr><td colSpan={4} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>No drawdowns</td></tr>}
                {(draws||[]).map(d=>(
                  <tr key={d.id}><td className="mono">{d.drawdown_ref}</td><td>{fmtDate(d.draw_date)}</td><td className="r fw">{fmt(d.amount)}</td><td style={{color:'var(--text3)'}}>{d.purpose||'—'}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <div className="col">
          <Card>
            <CardHead title="Facility details" />
            <div className="card-body">
              <div className="info-rows">
                <InfoRow label="Rate"       value={inst.interest_rate_desc||'—'} />
                <InfoRow label="Term"       value={inst.term_months?`${inst.term_months} months`:'—'} />
                <InfoRow label="Start date" value={fmtDate(inst.start_date)} />
                <InfoRow label="Maturity"   value={fmtDate(inst.maturity_date)} />
                <InfoRow label="Security"   value={inst.security||'—'} />
                <InfoRow label="Total repaid" value={fmt(inst.total_repaid||0)} />
              </div>
            </div>
          </Card>
          <Card>
            <CardHead title="Record drawdown" />
            <div className="card-body" style={{paddingTop:12}}>
              <div className="form-row fr2">
                <div><label className="field-label">Amount (ZAR) *</label><input className="field-input" type="number" min="1" placeholder="0.00" value={ddForm.amount} onChange={df('amount')} /></div>
                <div><label className="field-label">Draw date *</label><input className="field-input" type="date" value={ddForm.drawDate} onChange={df('drawDate')} /></div>
              </div>
              <div className="form-row"><div><label className="field-label">Purpose</label><input className="field-input" type="text" placeholder="e.g. Deal HC-2025-031" value={ddForm.purpose} onChange={df('purpose')} /></div></div>
              <div style={{display:'flex',justifyContent:'flex-end'}}>
                <button className="btn btn-p" onClick={saveDrawdown} disabled={saving}>{saving?'Saving…':'Record drawdown'}</button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* DEALS LIST                                                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const DealsPage = ({ onNavigate, showToast }) => {
  const [open, setOpen]           = useState(false);
  const [saving, setSaving]       = useState(false);
  const [statusFil, setStatusFil] = useState('');
  const [form, setForm] = useState({ clientName:'', entrepreneurName:'', sector:'', capitalRequired:'', legacyAllocation:'', institutionalAllocation:'', institutionId:'', facilityFeePct:'3.5', expectedRepaymentDate:'', riskRating:'medium', notes:'' });
  const { data:rows, loading, error, reload } = useApi(()=>dealsApi.list(statusFil?{status:statusFil}:{}), [statusFil]);
  const { data:instRows }                     = useApi(()=>instApi.list());
  const f = k => e => setForm(p=>({...p,[k]:e.target.value}));

  const save = async () => {
    if (!form.clientName || !form.capitalRequired) { showToast('Client name and capital required are mandatory','error'); return; }
    setSaving(true);
    try {
      const res = await dealsApi.create({ clientName:form.clientName, entrepreneurName:form.entrepreneurName||undefined, sector:form.sector||undefined, capitalRequired:parseFloat(form.capitalRequired), pinnedInstitutionId:form.institutionId||undefined, facilityFeePct:parseFloat(form.facilityFeePct||3.5)/100, expectedRepaymentDate:form.expectedRepaymentDate||undefined, riskRating:form.riskRating, notes:form.notes||undefined });
      showToast(`Deal ${res.data?.deal_ref} created — auto-allocated ${res.data?.legacy_allocation>0&&res.data?.institutional_allocation>0?'legacy + institutional':res.data?.legacy_allocation>0?'Legacy only':'Institutional only'}`);
      setOpen(false);
      setForm({clientName:'',entrepreneurName:'',sector:'',capitalRequired:'',institutionId:'',facilityFeePct:'3.5',expectedRepaymentDate:'',riskRating:'medium',notes:''});
      reload();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  return (
    <div>
      <div className="fbar">
        <select className="fsel" value={statusFil} onChange={e=>setStatusFil(e.target.value)}><option value="">All statuses</option><option value="active">Active</option><option value="pending">Pending</option><option value="overdue">Overdue</option><option value="new">New</option><option value="repaid">Repaid</option></select>
        <div className="fs" />
        <button className="btn btn-p" onClick={()=>setOpen(true)}>+ New deal</button>
      </div>
      <Card>
        <div style={{overflowX:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Deal ID</th><th>Client</th><th className="r">Required</th><th className="r">Allocated</th><th>Funding split</th><th>Repayment</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {!(rows||[]).length&&<tr><td colSpan={9} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No deals</td></tr>}
              {(rows||[]).map(d=>(
                <tr key={d.id} className="clickable" onClick={()=>onNavigate('deal-detail',{dealId:d.id})}>
                  <td className="mono">{d.deal_ref}</td>
                  <td><div style={{fontWeight:600}}>{d.client_name}</div><div style={{fontSize:'10.5px',color:'var(--text3)'}}>{d.sector||'—'}</div></td>
                  <td className="r">{fmt(d.capital_required)}</td>
                  <td className="r fw" style={{color:parseFloat(d.total_allocated)>=parseFloat(d.capital_required)?'var(--success)':'var(--warning)'}}>{fmt(d.total_allocated)}</td>
                  <td><SplitBar legacyPct={parseFloat(d.total_allocated)>0?Math.round(parseFloat(d.legacy_allocation)/parseFloat(d.total_allocated)*100):0} instPct={parseFloat(d.total_allocated)>0?Math.round(parseFloat(d.institutional_allocation)/parseFloat(d.total_allocated)*100):0} /></td>
                  <td style={{color:d.status==='overdue'?'var(--danger)':''}}>{fmtDate(d.expected_repayment_date)}{d.status==='overdue'?' (overdue)':''}</td>
                  <td><Badge type={d.status==='active'?'active':d.status==='overdue'?'danger':d.status==='new'?'info':'warn'}>{d.status}</Badge></td>
                  <td><button className="card-action">View →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Drawer open={open} onClose={()=>setOpen(false)} title="New deal">
        <div className="form-row fr2">
          <div><label className="field-label">Client / company name *</label><input className="field-input" type="text" placeholder="Client entity name" value={form.clientName} onChange={f('clientName')} /></div>
        </div>
        <div className="form-row fr2">
          <div><label className="field-label">Entrepreneur / contact</label><input className="field-input" type="text" placeholder="Full name" value={form.entrepreneurName} onChange={f('entrepreneurName')} /></div>
          <div><label className="field-label">Sector</label><select className="field-sel" value={form.sector} onChange={f('sector')}><option value="">— Select —</option><option>Technology</option><option>Retail / FMCG</option><option>Logistics</option><option>Manufacturing</option><option>Property</option><option>Agriculture</option><option>Other</option></select></div>
        </div>
        <div className="form-row fr2">
          <div><label className="field-label">Capital required (ZAR) *</label><input className="field-input" type="number" min="1" placeholder="0.00" value={form.capitalRequired} onChange={f('capitalRequired')} /></div>
          <div><label className="field-label">Facility fee (%)</label><input className="field-input" type="number" step="0.1" placeholder="3.5" value={form.facilityFeePct} onChange={f('facilityFeePct')} /></div>
        </div>
        <div style={{background:'var(--brand-light)',border:'1px solid var(--success-border)',borderRadius:7,padding:'10px 14px',marginBottom:14,fontSize:12,color:'var(--brand-dark)'}}>
          <strong>Auto-allocation enabled</strong> — each new deal is funded proportionally across the legacy pool and all institutions with available capacity. If one source is short, the others cover the remainder. You can optionally pin a specific institution below.
        </div>
        <SectionLabel style={{marginBottom:12}}>Optional overrides</SectionLabel>
        <div className="form-row"><div><label className="field-label">Pin institutional facility (optional)</label><select className="field-sel" value={form.institutionId} onChange={f('institutionId')}><option value="">— Auto-select best available —</option>{(instRows||[]).filter(i=>i.status==='active').map(i=><option key={i.id} value={i.id}>{i.name} · {fmt(i.available_facility)} available</option>)}</select></div></div>
        <div className="form-row fr2">
          <div><label className="field-label">Expected repayment</label><input className="field-input" type="date" value={form.expectedRepaymentDate} onChange={f('expectedRepaymentDate')} /></div>
          <div><label className="field-label">Risk rating</label><select className="field-sel" value={form.riskRating} onChange={f('riskRating')}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></div>
        </div>
        <div className="form-row"><div><label className="field-label">Notes</label><textarea className="field-ta" placeholder="Deal notes…" value={form.notes} onChange={f('notes')} /></div></div>
        <div className="form-actions">
          <button className="btn btn-o" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-p" onClick={save} disabled={saving}>{saving?'Saving…':'Create deal'}</button>
        </div>
      </Drawer>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* DEAL DETAIL                                                                 */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const DealDetailPage = ({ selectedIds, showToast, onNavigate }) => {
  const dealId = selectedIds?.dealId;
  const [repayModal, setRepayModal] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [repayForm, setRepayForm]   = useState({ repaymentDate:'', amountReceived:'', notes:'' });
  const [reallocating, setReallocating] = useState(false);

  const { data:deal,     loading, error, reload }  = useApi(()=>dealsApi.get(dealId),                  [dealId]);
  const { data:instPart, reload:reloadInstPart }   = useApi(()=>dealsApi.institutionAllocation(dealId), [dealId]);
  const { data:invPart,  reload:reloadInvPart }    = useApi(()=>dealsApi.participation(dealId),         [dealId]);

  if (!dealId) return <Err msg="Select a deal from the Deals list first" />;
  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  if (!deal)   return <Err msg="Deal not found" />;

  const capitalRequired = parseFloat(deal.capital_required || 0);
  const totalAllocated  = parseFloat(deal.total_allocated  || 0);
  const legacyAlloc     = parseFloat(deal.legacy_allocation || 0);

  // ── Rebuild the proportional table from stored allocation rows ──────────
  // instPart rows have: pool_share_pct, amount_allocated, available_at_time, institution_name
  // invPart  rows have: pool_share_pct, effective_exposure, full_name, investor_code
  const instRows   = Array.isArray(instPart) ? instPart : (instPart?.data || []);
  const invRows    = Array.isArray(invPart)  ? invPart  : (invPart?.data  || []);

  // Reconstruct all pools as they were at allocation time
  const poolsAtTime = [];
  if (legacyAlloc > 0) {
    const legacyPct = totalAllocated > 0 ? (legacyAlloc / totalAllocated * 100) : 0;
    poolsAtTime.push({
      poolType:   'legacy',
      poolName:   'Legacy Investor Pool',
      pct:        parseFloat(legacyPct.toFixed(2)),
      allocation: legacyAlloc,
      available:  instRows[0]?.available_at_time
        ? (legacyAlloc / totalAllocated) * totalAllocated
        : legacyAlloc,
    });
  }
  instRows.forEach(r => {
    poolsAtTime.push({
      poolType:   'institution',
      poolName:   `${r.institution_name} (${r.institution_code})`,
      pct:        parseFloat(Number(r.pool_share_pct).toFixed(2)),
      allocation: parseFloat(r.amount_allocated),
      available:  parseFloat(r.available_at_time),
    });
  });

  const COLOURS = ['#24695c','#f0a500','#1a5f8a','#7a3540','#4a4a7a','#7a5200','#2d8a6e'];

  const runReallocate = async () => {
    setReallocating(true);
    try {
      const res = await dealsApi.reallocate(dealId, {});
      showToast(`${deal.deal_ref} reallocated across ${res.allocation?.pools?.length || 0} pools`);
      reload(); reloadInstPart(); reloadInvPart();
    } catch(e) { showToast(e.message||'Failed','error'); }
    finally { setReallocating(false); }
  };

  const saveRepayment = async () => {
    if (!repayForm.repaymentDate || !repayForm.amountReceived) { showToast('Date and amount required','error'); return; }
    setSaving(true);
    try {
      await dealsApi.repayment(dealId, { repaymentDate:repayForm.repaymentDate, amountReceived:parseFloat(repayForm.amountReceived), notes:repayForm.notes||undefined });
      showToast('Repayment recorded — deal marked repaid'); setRepayModal(false); reload();
    } catch(e) { showToast(e.message||'Error','error'); }
    finally    { setSaving(false); }
  };

  return (
    <div>
      {/* ── Hero band ── */}
      <div className="deal-hero" style={{borderRadius:10,marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            <div style={{fontSize:11,opacity:0.5,letterSpacing:'0.5px',textTransform:'uppercase',marginBottom:3}}>Deal {deal.deal_ref}</div>
            <div style={{fontSize:17,fontWeight:700}}>{deal.client_name}</div>
            <div style={{fontSize:11.5,opacity:0.6,marginTop:3}}>{deal.sector||'—'} · Entrepreneur: {deal.entrepreneur_name||'—'}</div>
          </div>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <Badge type={deal.status==='active'?'active':deal.status==='overdue'?'danger':'warn'}>{deal.status}</Badge>
            {deal.status!=='repaid'&&<>
              <button className="btn" style={{background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',fontSize:11.5}} onClick={runReallocate} disabled={reallocating}>
                {reallocating?'Reallocating…':'↺ Reallocate'}
              </button>
              <button className="btn" style={{background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',fontSize:11.5}} onClick={()=>setRepayModal(true)}>Record repayment</button>
            </>}
          </div>
        </div>
        <div className="deal-kpi-row">
          {[
            ['Capital required', fmt(capitalRequired)],
            ['Total allocated',  fmt(totalAllocated)],
            ['Coverage',         capitalRequired>0?`${((totalAllocated/capitalRequired)*100).toFixed(1)}%`:'—'],
            ['Expected repayment',fmtDate(deal.expected_repayment_date)],
            ['Facility fee',     `${((parseFloat(deal.facility_fee_pct)||0)*100).toFixed(1)}%`],
            ['Fee earned',       fmt(deal.fee_earned||0)],
          ].map(([l,v])=>(
            <div className="dkpi" key={l}><div className="dkpi-label">{l}</div><div className="dkpi-val">{v}</div></div>
          ))}
        </div>
      </div>

      {/* ── Proportional Allocation Breakdown ── */}
      <Card className="mb">
        <CardHead
          title="Proportional allocation breakdown"
          sub="How this deal's funding was split across all pools at the time of allocation"
        />
        <div className="card-body" style={{paddingTop:14}}>

          {/* ── Allocation rule explanation box ── */}
          <div style={{background:'var(--brand-light)',border:'1px solid var(--success-border)',borderRadius:8,padding:'12px 16px',marginBottom:18,fontSize:12,lineHeight:1.7,color:'var(--brand-dark)'}}>
            <strong>Allocation rule:</strong> Each pool's contribution = (pool available capital ÷ total available capital across all pools) × deal amount required.
            This deal required <strong>{fmt(capitalRequired)}</strong>.
            {totalAllocated < capitalRequired && (
              <span style={{color:'var(--warning)',fontWeight:600}}> Note: shortfall of {fmt(capitalRequired - totalAllocated)} — insufficient pool capacity at allocation time.</span>
            )}
          </div>

          {/* ── Stacked bar ── */}
          {totalAllocated > 0 && (
            <div style={{marginBottom:18}}>
              <div style={{display:'flex',height:14,borderRadius:6,overflow:'hidden',marginBottom:8,border:'1px solid var(--border)'}}>
                {poolsAtTime.map((p,i) => (
                  <div key={i} title={`${p.poolName}: ${p.pct}%`}
                    style={{width:`${p.pct}%`,background:COLOURS[i%7],transition:'width 0.3s'}} />
                ))}
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'8px 20px'}}>
                {poolsAtTime.map((p,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:5,fontSize:11.5}}>
                    <span style={{width:10,height:10,borderRadius:2,background:COLOURS[i%7],display:'inline-block',flexShrink:0}} />
                    <span style={{color:'var(--text2)'}}>{p.poolName}</span>
                    <span style={{fontWeight:700,color:COLOURS[i%7]}}>{p.pct}%</span>
                    <span style={{color:'var(--text3)'}}>→ {fmt(p.allocation)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Calculation table ── */}
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Pool / Source</th>
                  <th>Type</th>
                  <th className="r">Available at time</th>
                  <th className="r">÷ Total available</th>
                  <th className="r">= Weight %</th>
                  <th className="r">× Deal amount</th>
                  <th className="r">= Allocation</th>
                </tr>
              </thead>
              <tbody>
                {poolsAtTime.length === 0 && (
                  <tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No allocation data — try clicking ↺ Reallocate to compute</td></tr>
                )}
                {poolsAtTime.map((p, i) => (
                  <tr key={i} style={{background:i%2===0?'var(--surface)':'var(--surface2)'}}>
                    <td style={{fontWeight:600}}>
                      <div style={{display:'flex',alignItems:'center',gap:7}}>
                        <span style={{width:8,height:8,borderRadius:'50%',background:COLOURS[i%7],flexShrink:0}} />
                        {p.poolName}
                      </div>
                    </td>
                    <td>
                      <Badge type={p.poolType==='legacy'?'active':'info'}>
                        {p.poolType==='legacy'?'Legacy Pool':'Institution'}
                      </Badge>
                    </td>
                    <td className="r" style={{color:'var(--text2)'}}>{p.available > 0 ? fmt(p.available) : <span style={{color:'var(--text3)',fontSize:11}}>At allocation time</span>}</td>
                    <td className="r" style={{color:'var(--text3)',fontSize:11}}>÷ {fmt(totalAllocated)}</td>
                    <td className="r">
                      <span style={{fontWeight:700,color:COLOURS[i%7],fontSize:13}}>{p.pct}%</span>
                    </td>
                    <td className="r" style={{color:'var(--text3)',fontSize:11}}>× {fmt(capitalRequired)}</td>
                    <td className="r">
                      <span style={{fontWeight:700,fontSize:13,color:'var(--brand)'}}>{fmt(p.allocation)}</span>
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                {poolsAtTime.length > 0 && (
                  <tr style={{background:'var(--brand-light)',borderTop:'2px solid var(--success-border)'}}>
                    <td colSpan={4} style={{fontWeight:700,padding:'9px 11px',color:'var(--brand-dark)'}}>Total</td>
                    <td className="r" style={{fontWeight:700,color:'var(--brand-dark)',fontSize:13}}>100%</td>
                    <td className="r" style={{fontWeight:700,color:'var(--brand-dark)'}}>{fmt(capitalRequired)}</td>
                    <td className="r" style={{fontWeight:700,color:'var(--brand-dark)',fontSize:13}}>{fmt(totalAllocated)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </Card>

      <div className="row3">
        <div className="col">
          {/* ── Legacy pool investor participation ── */}
          {legacyAlloc > 0 && (
            <Card>
              <CardHead title="Legacy pool — investor participation" sub="Each investor's proportional share of the legacy allocation" />
              <div style={{overflowX:'auto'}}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Investor</th>
                      <th className="r">Deposit balance</th>
                      <th className="r">Pool share %</th>
                      <th className="r">Effective exposure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!invRows.length && <tr><td colSpan={4} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No participation data</td></tr>}
                    {invRows.map(p => (
                      <tr key={p.investor_id}>
                        <td>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <Avatar initials={(p.full_name||'?').split(' ').map(w=>w[0]).join('').slice(0,2)} color={avatarColor(p.investor_id)} size={22} />
                            <div>
                              <div style={{fontWeight:600,fontSize:12}}>{p.full_name}</div>
                              <div style={{fontSize:10,color:'var(--text3)'}}>{p.investor_code}</div>
                            </div>
                          </div>
                        </td>
                        <td className="r" style={{color:'var(--text3)'}}>{fmt(p.investor_pool_balance)}</td>
                        <td className="r" style={{fontWeight:600}}>{Number(p.pool_share_pct).toFixed(2)}%</td>
                        <td className="r" style={{fontWeight:700,color:'var(--brand)'}}>{fmt(p.effective_exposure)}</td>
                      </tr>
                    ))}
                    {invRows.length > 0 && (
                      <tr style={{background:'var(--brand-light)',borderTop:'2px solid var(--success-border)'}}>
                        <td colSpan={3} style={{fontWeight:700,padding:'9px 11px',color:'var(--brand-dark)'}}>Legacy Pool total</td>
                        <td className="r" style={{fontWeight:700,color:'var(--brand-dark)'}}>{fmt(legacyAlloc)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{padding:'10px 14px',background:'var(--surface2)',borderTop:'1px solid var(--border)',fontSize:11,color:'var(--text3)'}}>
                Informational — individual investors are not direct counterparties. The Legacy Pool fund is the single counterparty.
              </div>
            </Card>
          )}

          {/* ── Repayment tracker ── */}
          <Card>
            <CardHead title="Repayment tracker" />
            <div className="card-body">
              <div className="stat-grid sg3" style={{marginBottom:10}}>
                <StatBox label="Amount due"      value={fmt(capitalRequired*(1+(parseFloat(deal.facility_fee_pct)||0)))} />
                <StatBox label="Days remaining"  value={deal.days_to_repayment!=null?`${deal.days_to_repayment}d`:'—'} />
                <StatBox label="Fee earned"      value={<span style={{color:'var(--success)'}}>{fmt(deal.fee_earned||0)}</span>} />
              </div>
              {deal.actual_repayment_date && (
                <div style={{background:'var(--success-bg)',border:'1px solid var(--success-border)',borderRadius:7,padding:'8px 12px',fontSize:12,color:'var(--success)'}}>
                  ✓ Repaid on {fmtDate(deal.actual_repayment_date)}
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="col">
          {/* ── Risk & details ── */}
          <Card>
            <CardHead title="Risk & profitability" />
            <div className="card-body">
              <div className="info-rows">
                <InfoRow label="Risk rating"  value={<Badge type={deal.risk_rating==='low'?'active':deal.risk_rating==='high'?'danger':'warn'}>{deal.risk_rating}</Badge>} />
                <InfoRow label="Sector"       value={deal.sector||'—'} />
                <InfoRow label="Facility fee" value={`${((parseFloat(deal.facility_fee_pct)||0)*100).toFixed(1)}%`} />
                <InfoRow label="Fee earned"   value={<span style={{color:'var(--success)'}}>{fmt(deal.fee_earned||0)}</span>} />
                <InfoRow label="Coverage"     value={<span style={{color:totalAllocated>=capitalRequired?'var(--success)':'var(--warning)',fontWeight:600}}>{capitalRequired>0?`${((totalAllocated/capitalRequired)*100).toFixed(1)}%`:'—'}</span>} />
              </div>
            </div>
          </Card>

          {/* ── Audit trail ── */}
          <Card>
            <CardHead title="Audit trail" />
            <div className="card-body" style={{paddingTop:10}}>
              {(deal.auditTrail||[]).length===0
                ? <div style={{color:'var(--text3)',fontSize:12,padding:'8px 0'}}>No audit entries</div>
                : <Timeline items={(deal.auditTrail||[]).slice(0,6).map(a=>({
                    title: a.action?.replace('.',' — ').replace(/\b\w/g,l=>l.toUpperCase()),
                    date:  `${new Date(a.timestamp).toLocaleString('en-ZA')} · ${a.user_name||'System'}`,
                  }))} />
              }
            </div>
          </Card>
        </div>
      </div>

      {/* ── Repayment modal ── */}
      <Modal open={repayModal} onClose={()=>setRepayModal(false)} title={`Record repayment — ${deal.deal_ref}`}>
        <div className="form-row fr2">
          <div><label className="field-label">Repayment date *</label><input className="field-input" type="date" value={repayForm.repaymentDate} onChange={e=>setRepayForm(p=>({...p,repaymentDate:e.target.value}))} /></div>
          <div><label className="field-label">Amount received (ZAR) *</label><input className="field-input" type="number" min="1" placeholder="0.00" value={repayForm.amountReceived} onChange={e=>setRepayForm(p=>({...p,amountReceived:e.target.value}))} /></div>
        </div>
        <div className="form-row"><div><label className="field-label">Notes</label><textarea className="field-ta" placeholder="Repayment notes…" value={repayForm.notes} onChange={e=>setRepayForm(p=>({...p,notes:e.target.value}))} /></div></div>
        <div className="form-actions">
          <button className="btn btn-o" onClick={()=>setRepayModal(false)}>Cancel</button>
          <button className="btn btn-p" onClick={saveRepayment} disabled={saving}>{saving?'Saving…':'Record repayment'}</button>
        </div>
      </Modal>
    </div>
  );
};

export const WithdrawalsPage = ({ showToast }) => {
  const [statusFil, setStatusFil] = useState('');
  const { data:rows, loading, error, reload } = useApi(()=>wdApi.list(statusFil?{status:statusFil}:{}), [statusFil]);

  const approve = async (id, ref) => {
    try { await wdApi.approve(id); showToast(`${ref} approved`); reload(); }
    catch(e) { showToast(e.message||'Error','error'); }
  };
  const reject = async (id, ref) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    try { await wdApi.reject(id, reason); showToast(`${ref} rejected`); reload(); }
    catch(e) { showToast(e.message||'Error','error'); }
  };
  const process = async (id) => {
    try { await wdApi.process(id,{}); showToast('Marked processed'); reload(); }
    catch(e) { showToast(e.message||'Error','error'); }
  };

  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  return (
    <div>
      <div className="fbar">
        <select className="fsel" value={statusFil} onChange={e=>setStatusFil(e.target.value)}><option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="processed">Processed</option></select>
        <div className="fs" />
      </div>
      <Card>
        <table className="tbl">
          <thead><tr><th>Ref</th><th>Investor</th><th>Type</th><th className="r">Amount</th><th>Requested</th><th>Approved by</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {!(rows||[]).length&&<tr><td colSpan={8} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No withdrawals</td></tr>}
            {(rows||[]).map(w=>(
              <tr key={w.id}>
                <td className="mono">{w.withdrawal_ref}</td>
                <td><div className="name-cell"><Avatar initials={(w.investor_name||'?').split(' ').map(x=>x[0]).join('').slice(0,2)} color={avatarColor(w.investor_id)} size={24} />{w.investor_name}</div></td>
                <td style={{fontSize:11.5}}>{w.withdrawal_type?.replace(/_/g,' ')}</td>
                <td className="r fw">{fmt(w.amount)}</td>
                <td>{fmtDate(w.requested_date)}</td>
                <td style={{color:'var(--text3)'}}>{w.approved_by_name||'—'}</td>
                <td><Badge type={w.status==='approved'||w.status==='processed'?'active':w.status==='pending'?'warn':'neutral'}>{w.status}</Badge></td>
                <td>
                  {w.status==='pending'&&<div style={{display:'flex',gap:4}}>
                    <button className="btn btn-p btn-sm" onClick={()=>approve(w.id,w.withdrawal_ref)}>Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={()=>reject(w.id,w.withdrawal_ref)}>Reject</button>
                  </div>}
                  {w.status==='approved'&&<button className="btn btn-o btn-sm" onClick={()=>process(w.id)}>Mark processed</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* REPORTS                                                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const ReportsPage = ({ showToast }) => {
  const [seg, setSeg]             = useState('Legacy');
  const [activeReport, setActive] = useState(null);
  const [reportData, setRepData]  = useState(null);
  const [loading, setLoading]     = useState(false);
  const [investorId, setInvId]    = useState('');
  const [instId, setInstId]       = useState('');
  const [days, setDays]           = useState('60');

  const { data:invList  } = useApi(()=>investorsApi.list());
  const { data:instList } = useApi(()=>instApi.list());

  const reportGroups = {
    Legacy:[
      { id:'investor-statement', icon:'◑', name:'Investor statement',    desc:'Deposits, returns and full balance per investor' },
      { id:'deposit-cycles',     icon:'◉', name:'Deposit cycle report',   desc:'All deposits with cycle dates and next payout projections' },
      { id:'return-accrual',     icon:'▤', name:'Return accrual summary', desc:'Aggregate accruing returns across all investors' },
      { id:'pool-utilisation',   icon:'⬡', name:'Pool utilisation',       desc:'Pool deployment rate and deal allocations' },
    ],
    Institutional:[
      { id:'amortisation',       icon:'◈', name:'Amortisation report',    desc:'Full schedule with paid/outstanding per facility' },
      { id:'facility-util',      icon:'▤', name:'Facility utilisation',   desc:'Facility size, drawdowns and available limits' },
    ],
    Portfolio:[
      { id:'deployed-capital',   icon:'◆', name:'Deployed capital',       desc:'All active deals with capital source breakdown' },
      { id:'repayments-due',     icon:'▦', name:'Repayments due',         desc:'Upcoming repayments across deals and facilities' },
    ],
  };

  const runReport = async (id) => {
    if (id === 'investor-statement' && !investorId) { showToast('Select an investor first','error'); return; }
    setLoading(true); setActive(id); setRepData(null);
    try {
      let res;
      if (id==='investor-statement')  res = await reports.investorStatement(investorId);
      else if (id==='deposit-cycles') res = await reports.depositCycles(investorId?{investorId}:{});
      else if (id==='return-accrual') res = await reports.returnAccrual();
      else if (id==='pool-utilisation') res = await reports.poolUtilisation();
      else if (id==='amortisation')   res = await reports.amortisation(instId?{institutionId:instId}:{});
      else if (id==='facility-util')  res = await reports.facilityUtil();
      else if (id==='deployed-capital') res = await reports.deployedCapital();
      else if (id==='repayments-due') res = await reports.repaymentsDue(parseInt(days));
      setRepData(res?.data ?? res);
    } catch(e) { showToast(e.message||'Report failed','error'); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!reportData) return;
    const rows = Array.isArray(reportData) ? reportData
      : reportData.deposits || reportData.deals || reportData.dealBreakdown
      || reportData.data || [];
    if (!rows.length) { showToast('No data to export','error'); return; }
    const keys = Object.keys(rows[0]);
    const csv  = [keys.join(','), ...rows.map(r => keys.map(k => JSON.stringify(r[k]??'')).join(','))].join('\n');
    const url  = URL.createObjectURL(new Blob([csv], {type:'text/csv'}));
    const a    = document.createElement('a'); a.href=url; a.download=`${activeReport||'report'}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const renderTable = () => {
    if (loading) return <Loading />;
    if (!reportData) return null;

    if (activeReport === 'investor-statement') {
      const inv = (invList||[]).find(i=>i.id===investorId);
      return (
        <div>
          <StmtDocument inv={inv||{full_name:'Investor',investor_code:''}} investorId={investorId} showToast={showToast} />
        </div>
      );
    }

    if (activeReport === 'deposit-cycles') {
      const rows = Array.isArray(reportData) ? reportData : [];
      return <table className="tbl"><thead><tr><th>Deposit</th><th>Investor</th><th className="r">Balance</th><th className="r">Next return</th><th>Next cycle</th><th>Cycle #</th><th>Compound</th><th>Status</th></tr></thead>
        <tbody>{rows.map(d=><tr key={d.deposit_ref}><td className="mono">{d.deposit_ref}</td><td>{d.investor_name}</td><td className="r">{fmt(d.current_balance)}</td><td className="r" style={{color:'var(--success)'}}>{fmt(d.next_return_amount)}</td><td>{fmtDate(d.next_cycle_date)}</td><td style={{textAlign:'center'}}>{d.cycle_number}</td><td><Badge type={d.compounding?'active':'neutral'}>{d.compounding?'On':'Off'}</Badge></td><td><Badge type={d.deposit_status==='active'?'active':'neutral'}>{d.deposit_status}</Badge></td></tr>)}</tbody>
      </table>;
    }

    if (activeReport === 'return-accrual') {
      const rows = Array.isArray(reportData) ? reportData : [];
      return <table className="tbl"><thead><tr><th>Investor</th><th className="r">Balance</th><th className="r">Accrued</th><th>Deposits</th><th className="r">Yield %</th></tr></thead>
        <tbody>{rows.map(r=><tr key={r.investor_code}><td><div style={{fontWeight:600}}>{r.investor_name}</div><div style={{fontSize:'10.5px',color:'var(--text3)'}}>{r.investor_code}</div></td><td className="r">{fmt(r.total_balance)}</td><td className="r" style={{color:'var(--success)',fontWeight:700}}>{fmt(r.total_accrued)}</td><td style={{textAlign:'center'}}>{r.deposit_count}</td><td className="r">{Number(r.return_yield_pct||0).toFixed(2)}%</td></tr>)}</tbody>
      </table>;
    }

    if (activeReport === 'pool-utilisation') {
      const d = reportData;
      return (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
            {[['Pool total',fmt(d.poolTotal)],['Deployed',fmt(d.deployed)],['Available',fmt(d.available)],['Utilisation',`${d.utilisationPct||0}%`]].map(([l,v])=>(
              <div key={l} className="stat-box"><div className="stat-label">{l}</div><div className="stat-val">{v}</div></div>
            ))}
          </div>
          <table className="tbl"><thead><tr><th>Deal</th><th>Client</th><th className="r">Legacy alloc.</th><th>Legacy %</th><th>Status</th><th>Repayment</th></tr></thead>
            <tbody>{(d.dealBreakdown||[]).map(d=><tr key={d.deal_ref}><td className="mono">{d.deal_ref}</td><td>{d.client_name}</td><td className="r fw">{fmt(d.legacy_allocation)}</td><td>{d.legacy_pct}%</td><td><Badge type={d.status==='active'?'active':d.status==='overdue'?'danger':'warn'}>{d.status}</Badge></td><td>{fmtDate(d.expected_repayment_date)}</td></tr>)}</tbody>
          </table>
        </div>
      );
    }

    if (activeReport === 'amortisation') {
      const rows = Array.isArray(reportData) ? reportData : [];
      return <table className="tbl"><thead><tr><th>Institution</th><th>Date</th><th>#</th><th className="r">Principal</th><th className="r">Interest</th><th className="r">Total</th><th className="r">Balance</th><th>Status</th></tr></thead>
        <tbody>{rows.map(r=><tr key={r.id} className={r.status==='paid'?'amort-paid':r.status==='due'?'amort-due':''}><td style={{fontSize:11}}>{r.institution_name}</td><td>{r.due_date}</td><td>{r.instalment_no}</td><td className="r">{fmt(r.principal)}</td><td className="r">{fmt(r.interest)}</td><td className="r">{fmt(r.total_due)}</td><td className="r">{fmt(r.balance_after)}</td><td><Badge type={r.status==='paid'?'neutral':r.status==='due'?'danger':'info'}>{r.status}</Badge></td></tr>)}</tbody>
      </table>;
    }

    if (activeReport === 'facility-util') {
      const rows = Array.isArray(reportData) ? reportData : [];
      return <table className="tbl"><thead><tr><th>Institution</th><th>Type</th><th className="r">Approved</th><th className="r">Drawn</th><th className="r">Available</th><th className="r">Utilisation</th><th>Status</th><th>Maturity</th></tr></thead>
        <tbody>{rows.map(r=><tr key={r.institution_code}><td><div style={{fontWeight:600}}>{r.name}</div><div style={{fontSize:'10.5px',color:'var(--text3)'}}>{r.institution_code}</div></td><td style={{fontSize:11}}>{r.facility_type?.replace(/_/g,' ')}</td><td className="r">{fmt(r.approved_facility)}</td><td className="r">{fmt(r.amount_drawn)}</td><td className="r" style={{color:'var(--success)'}}>{fmt(r.available)}</td><td className="r fw">{r.utilisation_pct}%<div style={{height:4,background:'var(--border)',borderRadius:2,marginTop:3,overflow:'hidden'}}><div style={{height:'100%',background:'var(--accent)',width:`${r.utilisation_pct}%`}} /></div></td><td><Badge type={r.status==='active'?'active':r.status==='review'?'warn':'neutral'}>{r.status}</Badge></td><td style={{fontSize:11}}>{fmtDate(r.maturity_date)}</td></tr>)}</tbody>
      </table>;
    }

    if (activeReport === 'deployed-capital') {
      const d = reportData;
      const rows = Array.isArray(d) ? d : (d?.data || d?.dealBreakdown || []);
      const totals = d?.totals;
      return (
        <div>
          {totals && <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
            {[['Total deployed',fmt(totals.total_deployed)],['Legacy',fmt(totals.total_legacy)],['Institutional',fmt(totals.total_institutional)],['Total fees',fmt(totals.total_fees)]].map(([l,v])=>(
              <div key={l} className="stat-box"><div className="stat-label">{l}</div><div className="stat-val">{v}</div></div>
            ))}
          </div>}
          <table className="tbl"><thead><tr><th>Deal</th><th>Client</th><th className="r">Required</th><th className="r">Legacy</th><th className="r">Institutional</th><th className="r">Fee earned</th><th>Risk</th><th>Status</th></tr></thead>
            <tbody>{rows.map(d=><tr key={d.deal_ref}><td className="mono">{d.deal_ref}</td><td><div style={{fontWeight:600}}>{d.client_name}</div><div style={{fontSize:'10.5px',color:'var(--text3)'}}>{d.sector||'—'}</div></td><td className="r">{fmt(d.capital_required)}</td><td className="r">{fmt(d.legacy_allocation)}</td><td className="r">{fmt(d.institutional_allocation)}</td><td className="r" style={{color:'var(--success)'}}>{fmt(d.fee_earned)}</td><td><Badge type={d.risk_rating==='low'?'active':d.risk_rating==='high'?'danger':'warn'}>{d.risk_rating}</Badge></td><td><Badge type={d.status==='active'?'active':d.status==='overdue'?'danger':'warn'}>{d.status}</Badge></td></tr>)}</tbody>
          </table>
        </div>
      );
    }

    if (activeReport === 'repayments-due') {
      const d = reportData;
      return (
        <div>
          <div style={{marginBottom:14}}>
            <div className="section-label" style={{marginBottom:8}}>Deal repayments</div>
            <table className="tbl"><thead><tr><th>Deal</th><th>Client</th><th className="r">Amount</th><th>Due date</th><th>Days left</th><th>Risk</th><th>Status</th></tr></thead>
              <tbody>{!(d?.deals||[]).length&&<tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No deal repayments due</td></tr>}
              {(d?.deals||[]).map(r=><tr key={r.deal_ref}><td className="mono">{r.deal_ref}</td><td>{r.client_name}</td><td className="r fw">{fmt(r.total_allocated)}</td><td style={{color:r.days_remaining<=0?'var(--danger)':r.days_remaining<=14?'var(--warning)':''}}>{fmtDate(r.expected_repayment_date)}</td><td style={{color:r.days_remaining<=0?'var(--danger)':'',fontWeight:r.days_remaining<=0?700:400}}>{r.days_remaining<=0?`${Math.abs(r.days_remaining)}d overdue`:`${r.days_remaining}d`}</td><td><Badge type={r.risk_rating==='low'?'active':r.risk_rating==='high'?'danger':'warn'}>{r.risk_rating}</Badge></td><td><Badge type={r.status==='overdue'?'danger':'active'}>{r.status}</Badge></td></tr>)}</tbody>
            </table>
          </div>
          <div style={{marginTop:16}}>
            <div className="section-label" style={{marginBottom:8}}>Institutional repayments</div>
            <table className="tbl"><thead><tr><th>Institution</th><th className="r">Amount due</th><th>Due date</th><th>Days left</th><th>Status</th></tr></thead>
              <tbody>{!(d?.institutionalRepayments||[]).length&&<tr><td colSpan={5} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No institutional repayments due</td></tr>}
              {(d?.institutionalRepayments||[]).map((r,i)=><tr key={i}><td>{r.institution_name}</td><td className="r fw">{fmt(r.total_due)}</td><td style={{color:r.days_remaining<=0?'var(--danger)':r.days_remaining<=14?'var(--warning)':''}}>{fmtDate(r.due_date)}</td><td style={{color:r.days_remaining<=0?'var(--danger)':''}}>{r.days_remaining<=0?`${Math.abs(r.days_remaining)}d overdue`:`${r.days_remaining}d`}</td><td><Badge type={r.status==='overdue'?'danger':r.status==='due'?'warn':'info'}>{r.status}</Badge></td></tr>)}</tbody>
            </table>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="seg-ctrl">
        {Object.keys(reportGroups).map(s=>(
          <button key={s} className={`seg ${seg===s?'active':''}`} onClick={()=>{setSeg(s);setActive(null);setRepData(null);}}>{s}</button>
        ))}
      </div>

      <div className="report-grid">
        {(reportGroups[seg]||[]).map(r=>(
          <div className={`report-card ${activeReport===r.id?'active-report':''}`} key={r.name}
            style={activeReport===r.id?{borderColor:'var(--brand)',borderWidth:2}:{}}
            onClick={()=>setActive(r.id)}>
            <div className="report-icon">{r.icon}</div>
            <div className="report-name">{r.name}</div>
            <div className="report-desc">{r.desc}</div>
          </div>
        ))}
      </div>

      {activeReport && (
        <Card className="mb">
          <CardHead title="Report parameters & output" />
          <div className="card-body" style={{paddingTop:12}}>
            <div className="fbar" style={{marginBottom:16}}>
              {/* Investor selector - for legacy reports */}
              {seg==='Legacy' && (
                <select className="fsel" value={investorId} onChange={e=>setInvId(e.target.value)}>
                  <option value="">All investors</option>
                  {(invList||[]).map(i=><option key={i.id} value={i.id}>{i.full_name} ({i.investor_code})</option>)}
                </select>
              )}
              {/* Institution selector - for institutional reports */}
              {seg==='Institutional' && (
                <select className="fsel" value={instId} onChange={e=>setInstId(e.target.value)}>
                  <option value="">All institutions</option>
                  {(instList||[]).map(i=><option key={i.id} value={i.id}>{i.name} ({i.institution_code})</option>)}
                </select>
              )}
              {/* Days selector for repayments-due */}
              {activeReport==='repayments-due' && (
                <select className="fsel" value={days} onChange={e=>setDays(e.target.value)}>
                  <option value="30">Next 30 days</option>
                  <option value="60">Next 60 days</option>
                  <option value="90">Next 90 days</option>
                  <option value="180">Next 6 months</option>
                </select>
              )}
              <div className="fs" />
              <button className="btn btn-p" onClick={()=>runReport(activeReport)} disabled={loading}>{loading?'Generating…':'Generate report'}</button>
              {reportData && activeReport!=='investor-statement' && (
                <button className="btn btn-o" onClick={exportCSV}>Export CSV</button>
              )}
            </div>
            <div style={{overflowX:'auto'}}>
              {renderTable()}
              {!loading && !reportData && (
                <div style={{textAlign:'center',padding:'32px 0',color:'var(--text3)',fontSize:13}}>
                  Select parameters above and click Generate report
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* NOTIFICATIONS                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const NotificationsPage = () => {
  const { data:activity, loading } = useApi(()=>dashboardApi.activity());
  const { data:obls }              = useApi(()=>dashboardApi.obligations());
  return (
    <div>
      <div className="row2">
        <Card>
          <CardHead title="Upcoming obligations" sub="Next 60 days" />
          {loading?<div style={{padding:20}}><Loading /></div>:(
            <div className="card-body">
              {!(obls||[]).length&&<div style={{color:'var(--text3)',fontSize:13,padding:'8px 0'}}>No obligations in the next 60 days</div>}
              {(obls||[]).map((o,i)=>(
                <div className="obl-item" key={i}>
                  <div className="obl-date" style={{background:new Date(o.due_date)<=new Date()?'var(--danger-bg)':'var(--surface2)',color:new Date(o.due_date)<=new Date()?'var(--danger)':'var(--text3)'}}>
                    {new Date(o.due_date).toLocaleDateString('en-ZA',{day:'numeric',month:'short'})}
                  </div>
                  <div className="obl-info"><div className="obl-name">{o.description}</div><div className="obl-type">{o.type?.replace(/_/g,' ')}</div></div>
                  <div className="obl-amt">{fmt(o.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <CardHead title="Recent activity" />
          {loading?<div style={{padding:20}}><Loading /></div>:(
            <div className="card-body">
              {(activity||[]).slice(0,8).map((a,i)=>(
                <div className="act-item" key={i}>
                  <div className="act-icon" style={{background:'var(--brand-light)'}}>◉</div>
                  <div style={{flex:1}}><div className="act-text"><b>{a.action?.replace('.',' — ')}</b></div><div className="act-time">{new Date(a.timestamp).toLocaleString('en-ZA')} · {a.user_name}</div></div>
                </div>
              ))}
              {!(activity||[]).length&&<div style={{color:'var(--text3)',fontSize:13}}>No recent activity</div>}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* AUDIT TRAIL                                                                 */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const AuditPage = () => {
  const [mod, setMod]       = useState('');
  const [days, setDays]     = useState('30');
  const [search, setSearch] = useState('');
  const { data, loading, error, reload } = useApi(()=>auditApi.list({...(mod&&{module:mod}),days,...(search&&{search})}), [mod,days,search]);
  if (loading) return <Loading />;
  if (error)   return <Err msg={error} onRetry={reload} />;
  return (
    <div>
      <div className="fbar">
        <input className="finput" type="text" placeholder="Search log…" value={search} onChange={e=>setSearch(e.target.value)} />
        <select className="fsel" value={mod} onChange={e=>setMod(e.target.value)}><option value="">All modules</option>{['investors','deals','institutional','returns','withdrawals','settings','auth'].map(m=><option key={m} value={m}>{m}</option>)}</select>
        <select className="fsel" value={days} onChange={e=>setDays(e.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option></select>
        <div className="fs" />
      </div>
      <Card>
        <div style={{overflowX:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Module</th><th>Record</th><th>Trigger</th></tr></thead>
            <tbody>
              {!(data||[]).length&&<tr><td colSpan={6} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No audit entries</td></tr>}
              {(data||[]).map(a=>(
                <tr key={a.id}>
                  <td style={{color:'var(--text3)',fontSize:11}}>{new Date(a.timestamp).toLocaleString('en-ZA')}</td>
                  <td>{a.user_name||'System'}</td>
                  <td style={{fontWeight:600}}>{a.action}</td>
                  <td><Badge type="info">{a.module}</Badge></td>
                  <td className="mono">{a.record_ref||a.record_id||'—'}</td>
                  <td><Badge type={a.trigger_type==='integration'?'active':'neutral'}>{a.trigger_type}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* SETTINGS                                                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */
export const SettingsPage = ({ showToast, user }) => {
  const [saving,       setSaving]       = useState(false);
  const [disconnecting,setDisconnecting] = useState(false);
  const [refreshing,   setRefreshing]   = useState(false);
  const isAdmin = user?.role === 'admin';
  const { data:raw,   reload: reloadSettings } = useApi(()=>settingsApi.get());
  const { data:xeroStatus, reload: reloadXero } = useApi(()=>xeroApi.status());
  const gv = key => raw?.[key]?.value ?? '';

  const save = async (updates) => {
    setSaving(true);
    try { await settingsApi.update(updates); showToast('Settings saved'); reloadSettings(); }
    catch(e) { showToast(e.message||'Error','error'); }
    finally  { setSaving(false); }
  };

  const disconnectXero = async () => {
    if (!window.confirm('Disconnect Xero? Tokens will be revoked and removed from the database.')) return;
    setDisconnecting(true);
    try { await xeroApi.disconnect(); showToast('Xero disconnected'); reloadXero(); }
    catch(e) { showToast(e.message||'Error','error'); }
    finally  { setDisconnecting(false); }
  };

  const refreshXeroToken = async () => {
    setRefreshing(true);
    try {
      const r = await xeroApi.refresh();
      const info = r.data ?? r;
      showToast(`Token refreshed — expires in ${info.minsLeft} minutes`);
      reloadXero();
    }
    catch(e) { showToast(e.message||'Error','error'); }
    finally  { setRefreshing(false); }
  };

  const xero = xeroStatus || {};
  const tokenHealth = xero.connected
    ? (xero.isExpired ? 'danger' : xero.minsLeft < 10 ? 'warn' : 'active')
    : 'neutral';
  const tokenLabel  = xero.connected
    ? (xero.isExpired ? 'Token expired — will auto-refresh on next use' : `Expires in ${xero.minsLeft} min`)
    : 'Not connected';

  return (
    <div>
      {/* ── Legacy return rules ── */}
      <div className="settings-sect">
        <div className="ss-head"><div className="ss-title">Legacy return rules</div></div>
        <div className="ss-body">
          <div className="setting-row"><div className="sr-info"><div className="sr-name">Return rate</div><div className="sr-desc">Fixed percentage per cycle</div></div><div style={{display:'flex',alignItems:'center',gap:8}}><input className="si" type="number" step="0.1" defaultValue={parseFloat(gv('legacy.return_rate')||0.05)*100} onBlur={e=>save({'legacy.return_rate':(parseFloat(e.target.value)/100).toString()})} /><span style={{fontSize:12,color:'var(--text3)'}}>%</span></div></div>
          <div className="setting-row"><div className="sr-info"><div className="sr-name">Cycle duration (days)</div><div className="sr-desc">Days between return cycles</div></div><input className="si" type="number" defaultValue={gv('legacy.cycle_days')||60} onBlur={e=>save({'legacy.cycle_days':e.target.value})} /></div>
          <div className="setting-row"><div className="sr-info"><div className="sr-name">Automatic compounding</div><div className="sr-desc">Legacy deposits: 5% return is added to balance every 60 days when investor, dashboard, or journal pages are opened</div></div><Toggle on={gv('legacy.compounding_default')===true||gv('legacy.compounding_default')==='true'} onChange={v=>save({'legacy.compounding_default':v.toString()})} /></div>
          <div className="setting-row"><div className="sr-info"><div className="sr-name">Require payout approval</div><div className="sr-desc">Finance manager must approve before disbursement</div></div><Toggle on={gv('legacy.require_payout_approval')!==false&&gv('legacy.require_payout_approval')!=='false'} onChange={v=>save({'legacy.require_payout_approval':v.toString()})} /></div>
        </div>
      </div>

      {/* ── Xero Integration ── */}
      <div className="settings-sect">
        <div className="ss-head"><div className="ss-title">Xero integration</div></div>
        <div className="ss-body">
          {/* Connection status banner */}
          <div style={{background:xero.connected?'var(--success-bg)':'var(--surface2)',border:`1px solid ${xero.connected?'var(--success-border)':'var(--border)'}`,borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:10,height:10,borderRadius:'50%',background:xero.connected?(xero.isExpired?'var(--warning)':'var(--success)'):'var(--text3)',flexShrink:0}} />
              <div>
                <div style={{fontWeight:600,fontSize:12.5,color:xero.connected?'var(--success)':'var(--text2)'}}>
                  {xero.connected ? `Connected — ${xero.tenantName || xero.tenantId}` : 'Not connected to Xero'}
                </div>
                {xero.connected && (
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>
                    <Badge type={tokenHealth} style={{marginRight:6}}>{tokenLabel}</Badge>
                    &nbsp;·&nbsp; {xero.refreshCount} token refreshes &nbsp;·&nbsp; Connected {xero.connectedAt ? new Date(xero.connectedAt).toLocaleDateString('en-ZA') : ''}
                  </div>
                )}
                {!xero.connected && (
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>
                    Connect to post Xero journal entries directly from the Xero Journal page. Requires XERO_CLIENT_ID and XERO_CLIENT_SECRET in server/.env
                  </div>
                )}
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexShrink:0}}>
              {xero.connected ? (
                <>
                  <button className="btn btn-o btn-sm" onClick={refreshXeroToken} disabled={refreshing}>
                    {refreshing ? 'Refreshing…' : '↻ Refresh token'}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={disconnectXero} disabled={disconnecting}>
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-sm"
                  style={{background:'#13B5EA',color:'#fff',border:'none',fontWeight:600}}
                  onClick={() => xeroApi.connect()}
                >
                  Connect to Xero
                </button>
              )}
            </div>
          </div>

          {/* Token details when connected */}
          {xero.connected && (
            <div className="info-rows">
              <InfoRow label="Organisation"   value={xero.tenantName || '—'} />
              <InfoRow label="Tenant ID"      value={<span style={{fontFamily:'monospace',fontSize:11}}>{xero.tenantId}</span>} />
              <InfoRow label="Token expires"  value={xero.expiresAt ? new Date(xero.expiresAt).toLocaleString('en-ZA') : '—'} />
              <InfoRow label="Last refreshed" value={xero.lastRefreshed ? new Date(xero.lastRefreshed).toLocaleString('en-ZA') : 'Never'} />
              <InfoRow label="Token refreshes"value={xero.refreshCount || 0} />
              <InfoRow label="Scopes"         value={<span style={{fontSize:10.5,color:'var(--text3)'}}>{xero.scope || '—'}</span>} />
            </div>
          )}

          {/* Setup instructions when not connected */}
          {!xero.connected && (
            <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,padding:'12px 16px',fontSize:12,lineHeight:1.7,color:'var(--text2)'}}>
              <strong>Setup:</strong> Add these to <code>server/.env</code>, then click "Connect to Xero":
              <div style={{marginTop:8,fontFamily:'monospace',fontSize:11,background:'var(--surface)',padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)'}}>
                XERO_CLIENT_ID=your_app_client_id<br/>
                XERO_CLIENT_SECRET=your_app_client_secret<br/>
                XERO_REDIRECT_URI=http://localhost:5000/api/xero/callback<br/>
                APP_URL=http://localhost:3000
              </div>
              <div style={{marginTop:8,color:'var(--text3)'}}>Get credentials at <strong>developer.xero.com/app/manage</strong> — create a Web App with the redirect URI above. Tokens are stored in the database and auto-refresh every 30 minutes.</div>
            </div>
          )}
        </div>
      </div>

      {isAdmin && <UsersSection currentUser={user} showToast={showToast} />}

      {/* ── Role permissions ── */}
      <div className="settings-sect">
        <div className="ss-head"><div className="ss-title">Role permissions</div></div>
        <div className="ss-body">
          <div style={{overflowX:'auto'}}>
            <table className="tbl" style={{fontSize:12}}>
              <thead><tr><th>Module</th><th>Admin</th><th>Finance</th><th>Operations</th><th>Executive</th></tr></thead>
              <tbody>
                {[['Investors & deposits','Full','Full','View','View'],['Returns engine','Full','Full','View','—'],['Withdrawals','Full','Full','View','—'],['Deals & allocation','Full','Full','Full','View'],['Institutional capital','Full','Full','View','View'],['Reports','Full','Full','View','View'],['Audit trail','Full','View','View','View'],['Settings','Full','—','—','—']].map(([m,...roles])=>(
                  <tr key={m}><td>{m}</td>{roles.map((r,i)=><td key={i} style={{color:r==='Full'?'var(--success)':r==='—'?'var(--danger)':'',fontWeight:r==='Full'?600:400}}>{r}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {saving&&<div style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',background:'var(--brand)',color:'#fff',padding:'8px 16px',borderRadius:8,fontSize:12,zIndex:200}}>Saving…</div>}
    </div>
  );
};
/* ═══════════════════════════════════════════════════════════════════════════ */
export const AllocationPage = ({ onNavigate, showToast }) => {
  const [drillModal,  setDrillModal]   = useState(false);
  const [drillId,     setDrillId]      = useState(null);
  const [previewAmt,  setPreviewAmt]   = useState('');
  const [previewData, setPreviewData]  = useState(null);
  const [previewing,  setPreviewing]   = useState(false);
  const [reallocId,   setReallocId]    = useState(null);

  const { data: poolsRes, reload: reloadPools } = useApi(() => dealsApi.pools());
  const { data: dealsList, reload: reloadDeals } = useApi(() => dealsApi.list());
  const { data: kpis }   = useApi(() => dashboardApi.kpis());
  const { data: instPart } = useApi(() => drillId ? dealsApi.institutionAllocation(drillId) : Promise.resolve({ data: [] }), [drillId]);
  const { data: invPart  } = useApi(() => drillId ? dealsApi.participation(drillId)          : Promise.resolve({ data: [] }), [drillId]);

  const poolList       = poolsRes?.data || [];
  const poolDeployable = poolsRes?.totalAvailable || 0;
  const k              = kpis || {};
  const totalCapital   = parseFloat(k.totalCapitalUnderManagement || 0);
  const deployed       = parseFloat(k.deployedCapital || 0);
  const availableAcrossPools = Math.max(0, parseFloat(k.availableCapital ?? (totalCapital - deployed)));
  const COLOURS        = ['#24695c','#f0a500','#1a5f8a','#7a3540','#4a4a7a','#7a5200','#2d8a6e','#b87800'];

  const runPreview = async () => {
    if (!previewAmt || isNaN(parseFloat(previewAmt))) { showToast('Enter a valid amount', 'error'); return; }
    setPreviewing(true); setPreviewData(null);
    try { const r = await dealsApi.previewAllocation(parseFloat(previewAmt)); setPreviewData(r.data); }
    catch (e) { showToast(e.message || 'Failed', 'error'); }
    finally { setPreviewing(false); }
  };

  const runReallocate = async (id, ref) => {
    setReallocId(id);
    try { await dealsApi.reallocate(id, {}); showToast(`${ref} reallocated`); reloadDeals(); reloadPools(); }
    catch (e) { showToast(e.message || 'Failed', 'error'); }
    finally { setReallocId(null); }
  };

  return (
    <div>
      <div className="stat-grid sg4" style={{ marginBottom: 14 }}>
        <StatBox label="Total capital" value={fmt(k.totalCapitalUnderManagement)} accentLeft />
        <StatBox label="Available across all pools" value={fmt(availableAcrossPools)} sub={`${fmt(totalCapital)} total − ${fmt(deployed)} deployed`} />
        <StatBox label="Deployed" value={fmt(k.deployedCapital)} sub={`${k.utilisationPct || 0}% utilisation`} />
        <StatBox label="Active deals" value={k.activeDeals || 0} sub={`${k.overdueDeals || 0} overdue`} />
      </div>

      <Card className="mb">
        <CardHead title="Pool weights — current available capital" sub="Every new deal is funded proportionally. Weights update automatically as pools change." action onAction={() => { reloadPools(); reloadDeals(); }} actionLabel="↺ Refresh" />
        <div className="card-body" style={{ paddingTop: 12 }}>
          {poolList.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12 }}>No pools with available capital.</div>}
          {poolList.map((pool, idx) => (
            <div key={pool.poolId} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLOURS[idx % 8], flexShrink: 0 }} />
                <div style={{ flex: 1, fontWeight: 600, fontSize: 12.5 }}>{pool.poolName}</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{fmt(pool.available)}</div>
                <div style={{ background: COLOURS[idx % 8], color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 700 }}>{pool.pct?.toFixed(2)}%</div>
              </div>
              <div style={{ height: 7, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: COLOURS[idx % 8], width: `${Math.min(100, pool.pct || 0)}%`, borderRadius: 4 }} />
              </div>
            </div>
          ))}
          {poolList.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--brand-light)', border: '1px solid var(--success-border)', borderRadius: 7, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--brand-dark)' }}>Deployable across {poolList.length} pools</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-dark)' }}>{fmt(poolDeployable)}</span>
            </div>
          )}
        </div>
      </Card>

      <Card className="mb">
        <CardHead title="Allocation preview" sub="See how any amount would be split — nothing is saved" />
        <div className="card-body" style={{ paddingTop: 12 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>Capital required (ZAR):</span>
            <input className="field-input" type="number" min="1" placeholder="e.g. 500000" value={previewAmt} onChange={e => { setPreviewAmt(e.target.value); setPreviewData(null); }} onKeyDown={e => e.key === 'Enter' && runPreview()} style={{ width: 160 }} />
            <button className="btn btn-p" onClick={runPreview} disabled={previewing}>{previewing ? 'Calculating…' : 'Preview'}</button>
            {previewData && <span style={{ fontSize: 12, fontWeight: 600, color: previewData.shortfall > 0 ? 'var(--warning)' : 'var(--success)' }}>{previewData.shortfall > 0 ? `⚠ Shortfall ${fmt(previewData.shortfall)}` : `✓ Fully funded across ${previewData.pools?.length} pool${previewData.pools?.length !== 1 ? 's' : ''}`}</span>}
          </div>
          {previewData && (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl">
                <thead><tr><th>Pool</th><th>Type</th><th className="r">Available</th><th className="r">Weight %</th><th className="r">Allocation</th></tr></thead>
                <tbody>
                  {(previewData.pools || []).map((p, idx) => (
                    <tr key={p.poolId}>
                      <td style={{ fontWeight: 600 }}>{p.poolName}</td>
                      <td><Badge type={p.poolType === 'legacy' ? 'active' : 'info'}>{p.poolType === 'legacy' ? 'Legacy' : 'Institution'}</Badge></td>
                      <td className="r">{fmt(p.available)}</td>
                      <td className="r fw">{p.pct?.toFixed(2)}%</td>
                      <td className="r fw" style={{ color: 'var(--success)' }}>{fmt(p.allocation)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--brand-light)', borderTop: '2px solid var(--success-border)' }}>
                    <td colSpan={4} style={{ fontWeight: 700, padding: '9px 11px', color: 'var(--brand-dark)' }}>Total allocated</td>
                    <td className="r fw" style={{ color: 'var(--brand-dark)', fontSize: 13 }}>{fmt(previewData.totalAllocated)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <Card className="mb">
        <CardHead title="Current deal allocations" sub="Click any row to drill into the pool breakdown" />
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead><tr><th>Deal</th><th>Client</th><th className="r">Required</th><th className="r">Legacy</th><th className="r">Institutional</th><th className="r">Total</th><th>Split</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {!(dealsList || []).length && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>No deals</td></tr>}
              {(dealsList || []).map(d => {
                const req = parseFloat(d.capital_required || 0);
                const leg = parseFloat(d.legacy_allocation || 0);
                const inst = parseFloat(d.institutional_allocation || 0);
                const tot = parseFloat(d.total_allocated || 0);
                const lp = tot > 0 ? Math.round((leg / tot) * 100) : 0;
                const ip = tot > 0 ? Math.round((inst / tot) * 100) : 0;
                return (
                  <tr key={d.id} className="clickable" onClick={() => { setDrillId(d.id); setDrillModal(true); }}>
                    <td className="mono">{d.deal_ref}</td>
                    <td><div style={{ fontWeight: 600 }}>{d.client_name}</div><div style={{ fontSize: '10.5px', color: 'var(--text3)' }}>{d.sector || '—'}</div></td>
                    <td className="r">{fmt(req)}</td>
                    <td className="r">{leg > 0 ? fmt(leg) : '—'}</td>
                    <td className="r">{inst > 0 ? fmt(inst) : '—'}</td>
                    <td className="r fw" style={{ color: tot < req ? 'var(--warning)' : '' }}>{fmt(tot)}</td>
                    <td>
                      {tot > 0 && <><div style={{ display: 'flex', height: 7, borderRadius: 4, overflow: 'hidden', marginBottom: 2 }}>{leg > 0 && <div style={{ background: 'var(--brand)', width: `${lp}%` }} />}{inst > 0 && <div style={{ background: 'var(--accent)', width: `${ip}%` }} />}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>{leg > 0 ? `Leg ${lp}%` : ''}{leg > 0 && inst > 0 ? ' · ' : ''}{inst > 0 ? `Inst ${ip}%` : ''}</div></>}
                    </td>
                    <td><Badge type={d.status === 'active' ? 'active' : d.status === 'overdue' ? 'danger' : 'warn'}>{d.status}</Badge></td>
                    <td onClick={e => e.stopPropagation()}>
                      {d.status !== 'repaid' && d.status !== 'cancelled' && (
                        <button className="btn btn-o btn-sm" disabled={reallocId === d.id} onClick={() => runReallocate(d.id, d.deal_ref)}>{reallocId === d.id ? '…' : 'Reallocate'}</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={drillModal} onClose={() => { setDrillModal(false); setDrillId(null); }} title="Allocation drill-down" width={620}>
        {(instPart || []).length > 0 && (
          <>
            <div className="section-label" style={{ marginBottom: 10 }}>Institutional pool contributions</div>
            <table className="tbl" style={{ marginBottom: 16 }}>
              <thead><tr><th>Institution</th><th className="r">Weight at time</th><th className="r">Amount allocated</th><th className="r">Available at time</th></tr></thead>
              <tbody>
                {(instPart || []).map(r => (
                  <tr key={r.institution_id}>
                    <td><div style={{ fontWeight: 600 }}>{r.institution_name}</div><div style={{ fontSize: '10.5px', color: 'var(--text3)' }}>{r.institution_code}</div></td>
                    <td className="r">{Number(r.pool_share_pct).toFixed(2)}%</td>
                    <td className="r fw" style={{ color: 'var(--success)' }}>{fmt(r.amount_allocated)}</td>
                    <td className="r" style={{ color: 'var(--text3)' }}>{fmt(r.available_at_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div className="section-label" style={{ marginBottom: 10 }}>Legacy pool — investor participation</div>
        <table className="tbl">
          <thead><tr><th>Investor</th><th className="r">Pool share</th><th className="r">Effective exposure</th></tr></thead>
          <tbody>
            {!(invPart || []).length && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No legacy participation for this deal</td></tr>}
            {(invPart || []).map(p => (
              <tr key={p.investor_id}>
                <td><div style={{ fontWeight: 600 }}>{p.full_name}</div><div style={{ fontSize: '10.5px', color: 'var(--text3)' }}>{p.investor_code}</div></td>
                <td className="r">{Number(p.pool_share_pct).toFixed(2)}%</td>
                <td className="r fw">{fmt(p.effective_exposure)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/* XERO JOURNAL                                                                */
/* ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════ */
/* XERO JOURNAL                                                                */
/* ═══════════════════════════════════════════════════════════════════════════ */
// Account constants — shown in footer of journal card
const J_805 = '805 - Purchase Order - Investor Returns Payable';
const J_311 = '311 - COS - Deal Partner Return';

export const XeroJournalPage = ({ showToast, user }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [date,      setDate]    = useState(today);
  const [journal,   setJournal] = useState(null);
  const [loading,   setLoading] = useState(false);
  const [saving,    setSaving]  = useState(false);
  const [sending,   setSending] = useState(false);
  const [connecting,setConnecting] = useState(false);
  const [disconnecting,setDisconnecting] = useState(false);
  const [refreshing,setRefreshing] = useState(false);
  const [breakdownId, setBreakdownId] = useState(null);

  const canManageXero = user?.role === 'admin' || user?.role === 'finance';
  const investorRows = journal?.investors || journal?.investor_breakdown || [];
  const depositRows  = journal?.deposits  || journal?.deposit_breakdown  || [];
  const { data: xeroStatus, reload: reloadXero } = useApi(() => xeroApi.status());
  const { data: history, reload: reloadHistory } = useApi(() => journalApi.history());

  const xero = xeroStatus || {};
  const tokenHealth = xero.connected
    ? (xero.isExpired ? 'danger' : xero.minsLeft < 10 ? 'warn' : 'active')
    : 'neutral';
  const tokenLabel = xero.connected
    ? (xero.isExpired ? 'Token expired — auto-refreshes on send' : `Expires in ${xero.minsLeft} min`)
    : 'Not connected';

  const connectXero = async () => {
    setConnecting(true);
    try { await xeroApi.connect(); }
    catch (e) { showToast(e.message || 'Could not start Xero connection', 'error'); setConnecting(false); }
  };

  const disconnectXero = async () => {
    if (!window.confirm('Disconnect Xero? Tokens will be revoked and removed from the database.')) return;
    setDisconnecting(true);
    try { await xeroApi.disconnect(); showToast('Xero disconnected'); reloadXero(); }
    catch (e) { showToast(e.message || 'Error', 'error'); }
    finally { setDisconnecting(false); }
  };

  const refreshXeroToken = async () => {
    setRefreshing(true);
    try {
      const r = await xeroApi.refresh();
      const info = r.data ?? r;
      showToast(`Token refreshed — expires in ${info.minsLeft} minutes`);
      reloadXero();
    } catch (e) { showToast(e.message || 'Error', 'error'); }
    finally { setRefreshing(false); }
  };

  const fmtAmt = n => n != null
    ? `R\u00a0${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';

  const preview = async () => {
    setLoading(true);
    try {
      const r = await journalApi.preview(date);
      if (!r.data) { showToast(r.message || 'No active deposits', 'warn'); return; }
      setJournal(r.data);
      setBreakdownId(null);
    } catch (e) { showToast(e.message || 'Preview failed', 'error'); }
    finally     { setLoading(false); }
  };

  const generate = async () => {
    if (!journal) { showToast('Preview first', 'error'); return; }
    setSaving(true);
    try {
      const r = await journalApi.generate({ date, periodLabel: journal.periodLabel });
      showToast(r.message || 'Journal record saved');
      reloadHistory();
    } catch (e) { showToast(e.message || 'Failed', 'error'); }
    finally     { setSaving(false); }
  };

  const sendToXero = async () => {
    if (!journal) { showToast('Preview the journal first', 'error'); return; }
    setSending(true);
    try {
      const r = await journalApi.sendToXero({
        date,
        periodLabel:     journal.periodLabel,
        provisionAmount: journal.provisionAmount,
        lines:           journal.lines,
      });
      if (r.demo) {
        showToast(`Demo mode — ${r.message}`, 'warn');
      } else {
        showToast(`✓ Sent to Xero — Journal ID: ${r.xeroJournalId}`);
      }
      reloadHistory();
    } catch (e) { showToast(e.message || 'Xero send failed', 'error'); }
    finally     { setSending(false); }
  };

  const copy = () => {
    if (!journal) return;
    const rows = journal.lines.map(l =>
      `${l.description}\t${l.account}\t${l.taxRate}\t${l.debit != null ? l.debit.toFixed(2) : ''}\t${l.credit != null ? l.credit.toFixed(2) : ''}`
    ).join('\n');
    const text = [
      `Narration\t${journal.narration}`,
      `Date\t${journal.date}`,
      `Basis\t${journal.basisNote}`,
      '',
      `Description\tAccount\tTax Rate\tDebit ZAR\tCredit ZAR`,
      rows,
      '',
      `Subtotal\t\t\t${journal.subtotal.debit != null ? journal.subtotal.debit.toFixed(2) : ''}\t${journal.subtotal.credit != null ? journal.subtotal.credit.toFixed(2) : ''}`,
      `TOTAL\t\t\t${journal.total.debit != null ? journal.total.debit.toFixed(2) : ''}\t${journal.total.credit != null ? journal.total.credit.toFixed(2) : ''}`,
    ].join('\n');
    navigator.clipboard.writeText(text).then(() => showToast('Copied — paste directly into Xero manual journal'));
  };

  return (
    <div>
      {canManageXero && (
        <Card className="mb">
          <div style={{ background: xero.connected ? 'var(--success-bg)' : 'var(--surface2)', border: `1px solid ${xero.connected ? 'var(--success-border)' : 'var(--border)'}`, borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: xero.connected ? (xero.isExpired ? 'var(--warning)' : 'var(--success)') : 'var(--text3)', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 12.5, color: xero.connected ? 'var(--success)' : 'var(--text2)' }}>
                  {xero.connected ? `Xero connected — ${xero.tenantName || xero.tenantId}` : 'Xero not connected'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {xero.connected ? (
                    <><Badge type={tokenHealth}>{tokenLabel}</Badge> · Refresh token stored in database · auto-refreshes before each post</>
                  ) : (
                    'Connect once — access and refresh tokens are saved in the database for automatic reconnection.'
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              {xero.connected ? (
                <>
                  <button className="btn btn-o btn-sm" onClick={refreshXeroToken} disabled={refreshing}>
                    {refreshing ? 'Refreshing…' : '↻ Refresh token'}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={disconnectXero} disabled={disconnecting}>
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-sm"
                  style={{ background: '#13B5EA', color: '#fff', border: 'none', fontWeight: 600 }}
                  onClick={connectXero}
                  disabled={connecting}
                >
                  {connecting ? 'Redirecting…' : 'Connect to Xero'}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── Top row: controls + history ── */}
      <div className="row2" style={{ marginBottom: 16 }}>

        {/* Left: generate panel */}
        <Card>
          <CardHead title="Generate Xero journal entry" sub="2.5% monthly provision on all active investor deposits" />
          <div className="card-body" style={{ paddingTop: 12 }}>
            <div style={{ background:'var(--brand-light)', border:'1px solid var(--success-border)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, lineHeight:1.65, color:'var(--brand-dark)' }}>
              Monthly provision for investor return — <strong>2.5%</strong> of each investor&apos;s compounded effective balance (not original deposits only).<br />
              <strong>{J_805}</strong> → <span style={{color:'var(--success)',fontWeight:600}}>Credit</span> &nbsp;·&nbsp;
              <strong>{J_311}</strong> → <span style={{color:'var(--danger)',fontWeight:600}}>Debit</span><br />
              Accrual and cash basis · No VAT · Manually post or send direct to Xero.
            </div>
            <div className="form-row fr2">
              <div>
                <label className="field-label">Journal date</label>
                <input className="field-input" type="date" value={date}
                  onChange={e => { setDate(e.target.value); setJournal(null); }} />
              </div>
              <div style={{ display:'flex', alignItems:'flex-end' }}>
                <button className="btn btn-p" style={{ width:'100%' }} onClick={preview} disabled={loading}>
                  {loading ? 'Calculating…' : 'Preview journal'}
                </button>
              </div>
            </div>
            {journal && (
              <div className="stat-grid sg3" style={{ marginTop: 8 }}>
                <StatBox label="Investors"            value={journal.investorCount ?? investorRows.length} sub={`${journal.depositCount} active deposits`} />
                <StatBox label="Effective balance"    value={fmt(journal.totalEffectiveBalance || journal.totalDepositBalance)} sub="Compounded capital after withdrawals" />
                <StatBox label="Provision (2.5%)"     value={fmtAmt(journal.provisionAmount)}         accentLeft />
              </div>
            )}
          </div>
        </Card>

        {/* Right: history */}
        <Card>
          <CardHead title="Journal history" sub="Generated and sent entries" />
          <table className="tbl">
            <thead>
              <tr>
                <th>Period</th>
                <th className="r">Provision</th>
                <th style={{textAlign:'center'}}>Deps</th>
                <th>Xero</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {!(history || []).length && (
                <tr><td colSpan={5} style={{ textAlign:'center', padding:20, color:'var(--text3)' }}>No journals yet</td></tr>
              )}
              {(history || []).map(h => (
                <tr key={h.id}>
                  <td style={{ fontWeight:500 }}>{h.period}</td>
                  <td className="r fw" style={{ color:'var(--success)' }}>{fmtAmt(h.provisionAmount)}</td>
                  <td style={{ textAlign:'center' }}>{h.depositCount || '—'}</td>
                  <td>
                    {h.sentToXero
                      ? <Badge type="active">{h.mode==='demo'?'Demo sent':'Sent ✓'}</Badge>
                      : <Badge type="neutral">Not sent</Badge>
                    }
                  </td>
                  <td style={{ color:'var(--text3)', fontSize:11 }}>{h.generatedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* ── Journal preview card ── */}
      {journal && (
        <Card className="mb">
          <div className="card-head" style={{ paddingBottom:12, borderBottom:'1px solid var(--border)' }}>
            <div>
              <div className="card-title">Journal entry preview</div>
              <div className="card-sub">Review below — copy, save record, or send direct to Xero</div>
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button className="btn btn-o" onClick={copy}>Copy to clipboard</button>
              <button className="btn btn-o" onClick={generate} disabled={saving}>
                {saving ? 'Saving…' : 'Save record'}
              </button>
              <button
                className="btn btn-p"
                onClick={sendToXero}
                disabled={sending || !xero.connected}
                title={!xero.connected ? 'Connect to Xero first' : undefined}
                style={{ background:'#13B5EA', borderColor:'#0FA3D4' }}
              >
                {sending ? 'Sending to Xero…' : '↗ Send to Xero'}
              </button>
            </div>
          </div>

          <div className="card-body" style={{ paddingTop:16 }}>

            {/* Journal header meta */}
            <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 16px', marginBottom:18, fontSize:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:'5px 24px' }}>
                {[
                  ['Narration', journal.narration],
                  ['Date',      new Date(journal.date).toLocaleDateString('en-ZA', { day:'numeric', month:'long', year:'numeric' })],
                  ['Basis',     journal.basisNote],
                  ['Amounts',   journal.amountsNote],
                ].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <span style={{ color:'var(--text3)', fontWeight:500 }}>{k}</span>
                    <span style={{ fontWeight: k === 'Narration' ? 600 : 400 }}>{v}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div style={{ overflowX:'auto', marginBottom:18 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                <thead>
                  <tr style={{ background:'var(--surface2)', borderBottom:'1px solid var(--border2)' }}>
                    {[
                      ['Description', '38%', 'left'],
                      ['Account',     '28%', 'left'],
                      ['Tax Rate',    '14%', 'left'],
                      ['Debit ZAR',   '10%', 'right'],
                      ['Credit ZAR',  '10%', 'right'],
                    ].map(([h, w, a]) => (
                      <th key={h} style={{ textAlign:a, padding:'8px 12px', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:'0.5px', color:'var(--text3)', width:w }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(journal.lines || []).map((l, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'11px 12px' }}>{l.description}</td>
                      <td style={{ padding:'11px 12px', color:'var(--text2)', fontSize:11.5 }}>{l.account}</td>
                      <td style={{ padding:'11px 12px', color:'var(--text3)' }}>{l.taxRate}</td>
                      <td style={{ padding:'11px 12px', textAlign:'right', fontWeight: l.debit != null ? 700 : 400, color: l.debit != null ? 'var(--danger)' : 'var(--text3)', fontSize: l.debit != null ? 13 : 11 }}>
                        {l.debit != null ? l.debit.toFixed(2) : '—'}
                      </td>
                      <td style={{ padding:'11px 12px', textAlign:'right', fontWeight: l.credit != null ? 700 : 400, color: l.credit != null ? 'var(--success)' : 'var(--text3)', fontSize: l.credit != null ? 13 : 11 }}>
                        {l.credit != null ? l.credit.toFixed(2) : '—'}
                      </td>
                    </tr>
                  ))}
                  {/* Subtotal row */}
                  <tr style={{ borderTop:'2px solid var(--border2)', background:'var(--surface2)' }}>
                    <td colSpan={3} style={{ padding:'9px 12px', fontWeight:600 }}>Subtotal</td>
                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:600, color:'var(--text3)' }}>
                      {journal.subtotal.debit != null ? journal.subtotal.debit.toFixed(2) : '—'}
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:700 }}>
                      {journal.subtotal.credit != null ? journal.subtotal.credit.toFixed(2) : '—'}
                    </td>
                  </tr>
                  {/* TOTAL row */}
                  <tr style={{ background:'var(--brand-light)', borderTop:'1px solid var(--success-border)' }}>
                    <td colSpan={3} style={{ padding:'10px 12px', fontWeight:700, color:'var(--brand-dark)', fontSize:13 }}>TOTAL</td>
                    <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, color:'var(--text3)', fontSize:12 }}>
                      {journal.total.debit != null ? journal.total.debit.toFixed(2) : '—'}
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, color:'var(--brand-dark)', fontSize:13 }}>
                      {journal.total.credit != null ? journal.total.credit.toFixed(2) : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="section-label" style={{ marginBottom:8 }}>Investor support schedule — {journal.periodLabel}</div>
            <div style={{fontSize:11.5,color:'var(--text3)',marginBottom:10,lineHeight:1.6}}>
              One line per investor: provision = closing effective compounded balance × 2.5%.
              Includes prior returns, new deposits, and partial withdrawals. Use <strong>View breakdown</strong> for deposit-level audit detail.
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Investor</th>
                  <th className="r">Active deps</th>
                  <th className="r">Opening balance</th>
                  <th className="r">New deposits</th>
                  <th className="r">Withdrawals</th>
                  <th className="r" style={{color:'var(--brand)'}}>Closing balance</th>
                  <th className="r" style={{color:'var(--success)'}}>Provision 2.5%</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {investorRows.map((inv, i) => (
                  <React.Fragment key={inv.investor_id || inv.investor_code || i}>
                    <tr style={{background: i%2===0?'var(--surface)':'var(--surface2)'}}>
                      <td style={{ fontWeight:600 }}>{inv.investor_name}</td>
                      <td className="r">{inv.number_of_active_deposits}</td>
                      <td className="r">{fmt(inv.opening_effective_balance)}</td>
                      <td className="r" style={{color:inv.new_deposits_for_month>0?'var(--brand)':'var(--text3)'}}>
                        {inv.new_deposits_for_month > 0 ? fmt(inv.new_deposits_for_month) : '—'}
                      </td>
                      <td className="r" style={{color:inv.withdrawals_for_month>0?'var(--danger)':'var(--text3)'}}>
                        {inv.withdrawals_for_month > 0 ? `(${fmt(inv.withdrawals_for_month)})` : '—'}
                      </td>
                      <td className="r fw" style={{color:'var(--brand)'}}>{fmt(inv.closing_effective_balance)}</td>
                      <td className="r fw" style={{ color:'var(--success)' }}>{fmtAmt(inv.monthly_provision)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-o btn-sm"
                          onClick={() => setBreakdownId(breakdownId === inv.investor_id ? null : inv.investor_id)}
                        >
                          {breakdownId === inv.investor_id ? 'Hide' : 'View breakdown'}
                        </button>
                      </td>
                    </tr>
                    {breakdownId === inv.investor_id && (
                      <tr>
                        <td colSpan={8} style={{ padding:0, background:'var(--surface2)' }}>
                          <table className="tbl" style={{ margin:0, fontSize:11.5 }}>
                            <thead>
                              <tr>
                                <th>Deposit</th>
                                <th>Deposit date</th>
                                <th className="r">Original</th>
                                <th className="r">Compounded in balance</th>
                                <th className="r">New this month</th>
                                <th className="r">Withdrawals</th>
                                <th className="r">Effective</th>
                                <th className="r">Provision</th>
                                <th>Cycle</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(inv.deposits || depositRows.filter(d => d.investor_id === inv.investor_id)).map(dep => (
                                <tr key={dep.deposit_ref || dep.deposit_id}>
                                  <td className="mono">{dep.deposit_ref}</td>
                                  <td>{dep.deposit_date}</td>
                                  <td className="r">{fmt(dep.original_deposit_amount ?? dep.original_amount)}</td>
                                  <td className="r">{fmt(dep.compounded_return_included)}</td>
                                  <td className="r">{dep.new_deposits_for_month > 0 ? fmt(dep.new_deposits_for_month) : '—'}</td>
                                  <td className="r">{dep.withdrawals_for_month > 0 ? `(${fmt(dep.withdrawals_for_month ?? dep.withdrawal_this_month)})` : '—'}</td>
                                  <td className="r fw">{fmt(dep.effective_balance)}</td>
                                  <td className="r">{fmtAmt(dep.monthly_provision ?? dep.provision)}</td>
                                  <td><Badge type="neutral">{dep.state_label || `Cycle ${dep.cycle_number}`}</Badge></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                <tr style={{ background:'var(--brand-light)', borderTop:'2px solid var(--success-border)' }}>
                  <td colSpan={5} style={{ fontWeight:700, padding:'9px 11px', color:'var(--brand-dark)' }}>
                    Total ({journal.investorCount ?? investorRows.length} investors · {journal.depositCount} deposits)
                  </td>
                  <td className="r fw" style={{color:'var(--brand-dark)'}}>{fmt(journal.totalEffectiveBalance)}</td>
                  <td className="r fw" style={{ color:'var(--brand-dark)', fontSize:13 }}>{fmtAmt(journal.provisionAmount)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            {/* Footer note */}
            <div style={{ marginTop:12, padding:'9px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:7, fontSize:11.5, color:'var(--text3)' }}>
              Rate: 2.5% &nbsp;·&nbsp;
              <strong>{J_805}</strong> → <span style={{color:'var(--success)'}}>Credit</span> &nbsp;·&nbsp;
              <strong>{J_311}</strong> → <span style={{color:'var(--danger)'}}>Debit</span> &nbsp;·&nbsp;
              No VAT &nbsp;·&nbsp;
              To send live, connect Xero above (OAuth tokens stored in database; refresh token auto-renews access)
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
